import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
// 🔀 `toDateStr` was a PRIVATE copy of `toLocalISO` (declared LEGACY_DEBT group
// `a9681237`); deleted 2026-09-08 for the canonical one. The local NAME is kept
// by alias because it sits alongside this file's `parseDate` / `addDays` /
// `daysDiff`, which are NOT unified — those three genuinely disagree between the
// Gantt views and swapping them makes bars disappear (`utils/ganttDates.js`).
import { formatDateShort, toLocalISO as toDateStr } from '../../utils/dateFormat';
import { GRADE_COLORS, ALLOCATION_OPTIONS } from '../../constants/budget';
import { BTN_XS } from '../../constants/buttons';
import { useVatMode } from '../../hooks/useVatMode';
import useDragScroll from '../../hooks/useDragScroll';
import useMeasuredHeight from '../../hooks/useMeasuredHeight';
import useGanttScrollFrame from '../../hooks/useGanttScrollFrame';
import useGanttZoom from '../../hooks/useGanttZoom';
import useHeldRowOrder from '../../hooks/useHeldRowOrder';
import StickyScrollbar from '../ui/StickyScrollbar';
import {
  TOOLBAR_GROUP, TOOLBAR_SEGMENT_TRACK_COMPACT, TOOLBAR_SEGMENT_COMPACT, TOOLBAR_SEGMENT_OFF, TOOLBAR_SEGMENT_ON,
} from '../ui/Toolbar';
import { PAGE_SCROLL_PANE_X } from '../../constants/stickyTable';
import { buildDateHeaders } from '../../utils/ganttDates';
import GanttDateAxis, { GANTT_HEADER_HEIGHT, GANTT_AXIS_Z, GANTT_DRAG_Z, GANTT_PANEL_Z, GanttGrid, GanttTodayLine } from '../gantt/GanttDateAxis';
import GanttZoomControl from '../gantt/GanttZoomControl';
import GanttBarTooltip, { useGanttBarTooltip, GanttDragTooltip } from '../gantt/GanttBarTooltip';
import { runPositions, isPremiumPosition } from '../../utils/consecutiveDays';
// Monday-indexed, and shared with the crew day grid rather than re-spelt here —
// a second Sunday-first array would silently offset every date this writes.
import { DAY_INITIALS, DAY_NAMES } from './CrewDayGrid';
import { GANTT_CURSOR } from '../../constants/ganttCursors';

/** The toolbar's group separator — `ui/PageHeaderBand`'s hairline classes. */
const GANTT_TOOLBAR_HAIRLINE = 'hidden min-[1400px]:block h-5 w-px bg-gray-300 dark:bg-gray-600';
/** The Labour Flow (`pageScroll`) chart's header height — see `headerHeight`. */
export const CREW_PAGE_SCROLL_HEADER_HEIGHT = 32;

/**
 * The weekly rate a DRAG PREVIEW should multiply by. S2-C, 2026-08-21.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 THIS REPLACES THREE VERBATIM COPIES OF `member.grade_weekly_rate || 0`,
 * AND ALL THREE WERE ALREADY WRONG BEFORE THE DAY MODEL EXISTED.
 * ─────────────────────────────────────────────────────────────────────────
 * The bar label, the drag preview pushed to the parent table, and the drag
 * tooltip each computed `totalWeeks * (member.grade_weekly_rate || 0)`. That
 * IGNORES `weekly_rate_override`, so dragging an overridden member previewed a
 * cost at their GRADE's rate — a figure that jumps the moment the save comes
 * back, in whichever direction the override went. `resolveEffectiveRate`
 * (server, `helpers/crewCost.js`) has honoured the override at every cost site
 * for as long as the column has existed; these three never did.
 *
 * 🔑 `effective_weekly_rate` is PUBLISHED by `GET /api/budget/crew-members`,
 * the endpoint this view's rows come from, so this reports the server's rate
 * rather than opening a fourth opinion about what a member is paid. The
 * `grade_weekly_rate` fallback keeps an older/cached payload rendering exactly
 * what it renders today.
 * 🪤 `Number.isFinite`, never `||` — a genuinely ZERO effective rate is falsy,
 * and `||` would fall through to the grade rate and preview money for a member
 * who is deliberately unpaid.
 *
 * ⚠️ **WHAT THIS DOES NOT FIX, STATED SO IT IS NOT MISREAD AS CLOSED.** The
 * preview is `weeks × rate`, and under the day model labour is `days/5 × rate`.
 * For a member whose day rows are all PRESUMED those agree, because S2-A
 * re-derives their days from the dates the drag is changing. For a member with
 * ANY hand-recorded day they do NOT agree and cannot: their days are facts and
 * a date drag deliberately leaves them alone, so labour will not follow the
 * bar. The preview then over- or under-states until the save returns the real
 * figure. Declared in `crew-cost-path-parity-manifest.test.js` §4 rather than
 * papered over; closing it needs Task 6's day grid, where the user can move the
 * days themselves.
 */
/*
 * 🔓 EXPORTED 2026-08-21 (N3) SOLELY SO IT CAN BE PINNED, and the widening is
 * justified by what the alternative could actually reach: of its call sites,
 * TWO live inside pointer-drag handlers that jsdom cannot drive at all — so a
 * rendered test could cover only the hover tooltip and would be asserting
 * layout rather than the money rule. Reverting this function to
 * `member?.grade_weekly_rate || 0` re-opens the `weekly_rate_override` bug and
 * left the client suite green and the parity manifest 27/27 green; an
 * unexported pure function that the money surfaces read is a rule with no way
 * to fail.
 * 🔀 CORRECTED 2026-08-22: this said FOUR call sites, one of them inside
 * `BarLabel`. `BarLabel` is DELETED (owner ruling, see the block where it
 * stood) and there are now THREE — the drag preview pushed to the page, the
 * drag tooltip, and the hover tooltip's `Rate:` line.
 */
export function previewWeeklyRate(member) {
  const effective = Number(member?.effective_weekly_rate);
  if (Number.isFinite(effective)) return effective;
  return Number(member?.grade_weekly_rate) || 0;
}

/* ═════ THE LIVE-COST STATE — 2026-08-23 ════════════════════════
 *
 * The figures on an OPEN strip are the server's answer to
 * `POST /crew-members/:id/cost-preview` for the draft, and a request takes
 * time. Three states, and the page decides which — this component only draws
 * them (`dayEditPreviewState`).
 *
 * 🔑 `pending` KEEPS THE NUMBER AND MARKS IT; `failed` REPLACES IT WITH A DASH.
 * The distinction is the whole point. While a costing is in flight the last
 * server answer is the best available figure and is about to be superseded, so
 * showing it with a settling mark is honest. When a costing has FAILED there is
 * no answer for the draft on screen at all, and the saved figure would be **a
 * stale number presented as current** — the one thing this must never do.
 * 🪤 `—` is drawn rather than `formatMoney(null)`: `Number(null)` is `0`, so a
 * fallback through the formatter prints £0.00, which is a claim rather than an
 * absence. The same trap `useCrewCostPreview` records for its own `null`.
 *
 * 🎨 THE CHANNEL IS **TEXT**, and that is forced. In this directory
 * `opacity-*` already means two things (the crew table's dirty guard; worked
 * vs not inside the bar), `border-dashed` means "off", and SATURATION is the
 * inactive-row treatment — so a fourth meaning on any of them is unreadable.
 * Text needs no 1.4.11 contrast argument, cannot multiply with the per-day
 * fade, and is the channel the cost summary's own `unsaved` marker already
 * uses one level up. */
export const LIVE_COST_PENDING_MARK = '…';
export const LIVE_COST_ABSENT_MARK = '—';
export const LIVE_COST_PENDING_TITLE = 'Recosting this draft on the server…';
export const LIVE_COST_FAILED_TITLE =
  'This draft could not be costed. The saved figure is not shown, because the dates or days on screen have moved past it.';

/**
 * Smart bar label that sticks to the visible left edge of the bar
 * and progressively hides: price first, then grade, then truncates name.
 */
/* 🔻 `BarLabel` WAS HERE AND IS DELETED — owner ruling, 2026-08-22.
 *
 * Verbatim: *"now that we have weekends and 6th and 7th days showing its not
 * looking right having the person's name on the gantt bar. Please remove the
 * person's name and total price from the gantt bar and put the total price
 * after the person's name in the left column."*
 *
 * 🔑 THE WHOLE COMPONENT WENT, NOT JUST THE TWO STRINGS, AND THAT IS A
 * JUDGEMENT — say so if you revisit it. `BarLabel` rendered one of four
 * progressively-truncated strings: `name — grade price`, `name — grade`,
 * `name`, or a 6-character ellipsis of `name`. Strike the name and the price
 * and every branch collapses to the GRADE alone — and the grade is already
 * carried three other ways on the same row: the bar's own COLOUR (the legend
 * maps colour → grade), the grade dot in the label column, and the row's
 * `title`. Keeping a lone grade string would have preserved most of the
 * clutter the owner objected to for the least information on the bar, plus a
 * per-bar scroll listener and the stick-to-the-visible-edge machinery.
 *
 * 🟢 NOTHING WAS LOST THAT IS NOT SAID ELSEWHERE, and each was CHECKED
 * rather than assumed: the NAME moves to the label column (where it already
 * was); the PRICE moves after it; the GRADE keeps the three channels above;
 * and the LIVE DRAG figure survives in `dragTooltipContent`, which has
 * carried `Budget: <original> → <live>` since before this change and is
 * rendered on every drag — the label column now reads that same number for
 * the bar being dragged, so there is no second derivation and no lost
 * feedback.
 *
 * 🔻 CONSEQUENCE FOR THE MONEY RATCHET: `previewWeeklyRate` loses a call
 * site (BarLabel's live budget), so `CrewGanttView.previewRate.test.js` §2.2
 * falls 5 → 4 occurrences. That is a CONTRACT change, not a test repair, and
 * a ratchet that may only ever fall — the three surviving surfaces (the drag
 * preview pushed to the page, the drag tooltip, and the hover tooltip's
 * `Rate:` line) all still go through it.
 */
function getAllocationLabel(value) {
  const opt = ALLOCATION_OPTIONS.find(o => o.value === value);
  return opt ? opt.label : `${value}`;
}

function parseDate(str) {
  if (!str) return null;
  const s = typeof str === 'string' ? str.slice(0, 10) : str;
  return new Date(s + 'T00:00:00');
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function daysDiff(a, b) {
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

/* ══════════ THE "NO DATES SET" PLACEHOLDER — 2026-08-27, owner-designed ═════
 *
 * A crew member with no `start_date`/`end_date` used to be filtered out of
 * `validMembers` entirely (still is — see below) and vanish from the chart,
 * counted only in a "N member(s) without dates hidden" note. Owner: "it would
 * be great if they still appeard so the user can drag their dates around."
 *
 * Placed on the FIRST VISIBLE WEEK, never "the current week" — the timeline
 * range is derived from DATED members only (see the `timelineStart` memo),
 * so an undated production's "current week" can be off-canvas entirely.
 * Drawn Mon–Sun; a drag commits Mon–Fri (`end_date` is inclusive and a typed
 * weekend boundary IS worked, so a naive Mon–Sun commit would silently book a
 * 7-day week — 1.4x labour, unclamped, plus both weekend premium rates — for
 * a placeholder nobody had filled in).
 *
 * 🔑 THIS IS A GEOMETRY-ONLY WEEK, NEVER PERSISTED AS SUCH. Nothing here ever
 * writes `placeholderWeekStart` anywhere; it exists only until the first
 * drag, at which point `onMemberUpdate` writes a real Mon–Fri span through
 * the SAME mutation path a dated member's drag already uses, and the member
 * becomes an ordinary dated (presumed) member from then on. */

/** Monday at-or-before `date` — floors backward to the week `date` is in. */
function weekMondayOf(date) {
  const dow = date.getDay();
  const back = (dow + 6) % 7;
  return addDays(date, -back);
}

/**
 * The first Monday at-or-AFTER `start` — forward-only, never before it.
 * `timelineStart` is already Monday-snapped whenever any dated member
 * exists, so this is a no-op there. It only does real work in the
 * zero-dated-members fallback (`timelineStart = today`, UNSNAPPED): a plain
 * "floor to the nearest Monday" would walk BACKWARD off the left edge of a
 * chart that starts at today, drawing the placeholder off canvas on the
 * left. Forward-only guarantees the placeholder's week is always inside the
 * rendered range — i.e. actually "the first VISIBLE week".
 */
function firstVisibleMonday(start) {
  const dow = start.getDay();
  if (dow === 1) return new Date(start);
  const forward = 7 - ((dow + 6) % 7);
  return addDays(start, forward);
}

/** The one wording, fed to BOTH the native `title` and an `sr-only` span (the
 *  `InactiveBadge.js` device) so the tooltip and the accessible name cannot
 *  drift apart. */
export const NO_DATES_SET_LABEL = 'No dates set';
export function noDatesSetTitle(member) {
  const name = (member && member.name) || 'This member';
  return `${name} has no dates yet. Drag this block onto a week to give them `
    + 'Monday-to-Friday dates, or expand once dated to edit individual days.';
}

/** Appends an alpha channel to a `#rrggbb` hex colour. Used for the
 *  placeholder's faded fill — deliberately NOT the CSS `opacity` property,
 *  which would also fade the "No dates set" text sitting inside the same
 *  element and defeat the owner's ruling that text is the PRIMARY carrier.
 *  `alpha` is 0–1; two hex digits, clamped and padded. */
function hexWithAlpha(hex, alpha) {
  const a = Math.max(0, Math.min(1, alpha));
  const byte = Math.round(a * 255).toString(16).padStart(2, '0');
  return `${hex}${byte}`;
}

/* ═══════════════ PER-DAY FADE — owner ruling, 2026-08-22 ═══════════════
 *
 * Owner, verbatim: "by default the standard week is 5 day, monday to friday.
 * Saturday and sunday should be transparent by default as they are default
 * to off days unless the buyer/supervisor actually sets them to worked. so
 * when extending a gantt bar every sat and sun by default should be
 * slightly faded/transparent to show its off days." Asked whether this is
 * weekend-only or any non-worked day, he RULED: ANY NON-WORKED DAY. A day
 * the member works renders at the grade colour; a day they do not renders
 * faded — regardless of which day of the week it falls on.
 *
 * 🔑 RESOLVING "is this day worked": `member.days` (the SAME rows the
 * premium-mark run counter reads) is the truth when it exists — a date is
 * worked iff a row names it, `is_presumed` or not. When a member has NO day
 * rows AT ALL (legacy, or never edited), the default is Mon–Fri worked,
 * Sat/Sun off — the owner's stated default, and it must not require data.
 *
 * 🔻 THIS SUPERSEDES THE OLD CONTINUOUS WEEK-LEVEL FADE FOR WORKED WEEKS.
 * `generateWeekSegments`' `workedDays`/`allocation`-driven `shade` used to
 * dim the WHOLE week continuously (`0.4 + shade * 0.6`) — the owner had
 * previously said that whole-bar fade "works well I would like to keep it".
 * Per-day fading expresses the same information BETTER and keeping both
 * would contradict the owner's own worked picture (a 3-day week's Mon/Tue/Wed
 * drawn SOLID, not dimmed) — so for a week that is not OFF, worked days are
 * no longer dimmed by the allocation/day-count fraction at all; only whether
 * THIS day was worked decides its opacity. OFF WEEKS ARE UNTOUCHED — see the
 * `isOff` branch at the render site, still the grade hex at `opacity: 0.15`
 * with a dashed border, kept deliberately distinct from "some days worked".
 */

/** `FADED_DAY_OPACITY` reused from the old continuous fade's FLOOR value —
 * `0.4` was already the minimum opacity a non-off week could render at
 * (`0.4 + 0 * 0.6`), so it is a value production has already shown, not a
 * new number invented for this change. Chosen to sit clearly between a fully
 * OFF week's `0.15` (still the more extreme, "nothing here" treatment) and a
 * worked day's full `1`. A judgement call, recorded as one — the owner's
 * picture specifies solid-vs-faded, not an exact opacity. */
const WORKED_DAY_OPACITY = 1;
const FADED_DAY_OPACITY = 0.4;

/**
 * The member's worked CALENDAR DATES as a `Set`, or `null` when there is no
 * day data at all (signalling "use the Mon–Fri default" to every caller).
 * Built once per `generateWeekSegments` call rather than per day — O(days
 * recorded), not O(days recorded × days drawn).
 */
function buildWorkedDateSet(member, resolvedWorkedDates) {
  // 🔑 THE OPEN EDITOR'S RESOLVED SET WINS, AND IT IS THE ONLY WAY THE FADE AND
  // THE PAYLOAD CAN AGREE. It comes from `resolveWorkedDateSet` in
  // `utils/crewDayResolution.js` — overlay over baseline over the allocation
  // fallback — which is byte-for-byte what `buildDayPayload` walks. Passing
  // `member.days` here instead would draw the STORED schedule under toggles
  // showing the EDITED one: a picture of a schedule the user is not saving.
  if (resolvedWorkedDates instanceof Set) return resolvedWorkedDates;
  if (!Array.isArray(member.days) || member.days.length === 0) return null;
  const set = new Set();
  for (const row of member.days) {
    if (row && row.work_date) set.add(row.work_date);
  }
  return set;
}

/**
 * Is `dateStr` (with JS `Date#getDay()` value `dow`) a worked day? `workedSet
 * === null` means the member has no day rows at all — Mon–Fri (1–5) worked,
 * Sat/Sun (0, 6) off, the owner's stated default. Otherwise the set is the
 * truth: a date not in it is not worked, whatever day of the week it is —
 * this is what makes the rule "any non-worked day", not "weekends only".
 */
function isDayWorked(workedSet, dateStr, dow) {
  if (workedSet === null) return dow >= 1 && dow <= 5;
  return workedSet.has(dateStr);
}

/* ══════════════ THE DAY TOGGLES — a SECOND editor, 2026-08-22 ══════════════
 *
 * Owner: *"it would be nice to have an expand chevron next to the person name
 * on the gantt chart and when clicked their gantt chart expands to show
 * clickable days, then the buyer/supervisor can then turn individual days on
 * and off. Clicking the user's name still takes us to the full expanded table
 * view."* Then, given the trade-offs: **"clicks only on week view."**
 *
 * 🚨 THIS COMPONENT RESOLVES NO DAYS OF ITS OWN, AND THAT IS THE WHOLE POINT.
 * `crew_member_days` already has an editor — the crew day grid in
 * `pages/BudgetCostCodes.js` — and a second surface answering "which days does
 * this week hold" in its own words IS the defect that understated a member's
 * labour by ~48% on live staging in August 2026. So the page resolves the day
 * set ONCE through `utils/crewDayResolution.js` and hands it here as
 * `dayEditWorkedDates`, a plain `Set` of dates; `buildWorkedDateSet` prefers it
 * over `member.days`, so the SAME set drives the bar's per-day fade, the toggle
 * buttons' pressed state AND the `PUT /days` payload. **The fade IS the
 * payload, rendered** — there is no second resolution to disagree with.
 *
 * 🪤 WHAT WAS DELIBERATELY *NOT* UNIFIED, AND IT IS A REAL SEAM. `isDayWorked`
 * above defaults a member with NO day rows to Mon–Fri flat (an owner ruling),
 * while `buildWeekDayBaseline`'s no-rows fallback is ALLOCATION-driven
 * (`daysForPreset(dayCountForAllocation(alloc))` — Mon–Wed at 0.6). They agree
 * at allocation 1.0 and disagree below it. Collapsing them would move the fade
 * for members nobody is editing, which is a shipped ruling and not this
 * change's to move — so the resolver's answer is used ONLY while a member's
 * strip is open, where it is also what the save would post, and the ruled
 * default stands everywhere else. The seam is pinned by §6 of
 * `CrewGanttView.dayToggle.test.js` rather than hidden.
 *
 * 🔶 WEEK AND MONTH ZOOM. Quarter (5px/day) stays out. A day is 40px at week
 * and 16px at month against a 44px house touch target, and a mis-click here
 * changes somebody's wages — so MONTH IS AN EVALUATION, added 2026-08-22 at the
 * owner's request ("lets try having it work on month view as well and I'll
 * decide if its too cramped"), NOT a finding that 16px is safe. If it stays,
 * that is a deliberate exception to the touch-target rule and needs saying out
 * loud rather than inheriting silence from this constant.
 * Outside these zooms the chevron stays VISIBLE but DISABLED with a tooltip
 * naming the remedy — a control that silently disappears reads as a bug — and
 * an already-open strip renders that same sentence instead of toggles rather
 * than collapsing, which would look like the unsaved edit had been thrown away.
 *
 * 🔑 THE STRIP IS ITS OWN ROW BENEATH THE BAR, NOT AN OVERLAY ON IT. The bar
 * owns three pointer gestures already (left/right/body drag) and click-to-
 * toggle laid on top of them is a genuine conflict; put the toggles somewhere
 * the drag handlers are not and the conflict cannot arise. It also buys the
 * 44px height the 30px bar could never give. `useDragScroll` already skips
 * `button`, so a real `<button>` is pan-safe for free.
 */
export const DAY_EDIT_ZOOMS = ['week', 'month'];
/** @deprecated Kept so an external reader of the old single-zoom name still
 *  resolves to a real zoom rather than `undefined`. Read `DAY_EDIT_ZOOMS`. */
export const DAY_EDIT_ZOOM = DAY_EDIT_ZOOMS[0];
/** 44px — the house minimum touch target, and the strip's whole height budget.
 *  ⚠️ The WIDTH cannot meet it: a toggle must be exactly as wide as the day it
 *  represents or it stops pointing at that day. 40px at week zoom was already
 *  under; MONTH IS 16px, well under, and that is the whole question the owner
 *  is evaluating. The width is the one dimension that is not ours to choose. */
export const DAY_TOGGLE_ROW_HEIGHT = 44;
/**
 * 6px of breathing room BELOW the open day strip — owner, 2026-08-23:
 * *"above and below each gantt bar we have a few pixels of gap. please add
 * this same gap to below the expanded gantt bar day picker."*
 *
 * 🔑 IT IS THE BAR'S OWN GAP, NOT A NEW NUMBER. The bar is `top-1.5` (6px)
 * inside a 42px row at `barHeight = rowHeight - 12`, i.e. 6 above and 6 below.
 * This is that same 6.
 *
 * 🪤 THE GAP COMES FROM EXTRA ROW HEIGHT, NEVER FROM A SHORTER STRIP.
 * `DAY_TOGGLE_ROW_HEIGHT` is the house 44px minimum touch target and shrinking
 * it to 38 to make room would trade an accessibility floor for a cosmetic gap —
 * on a control where a mis-click changes somebody's wages.
 *
 * ⚠️ SO IT MUST BE ADDED IN BOTH ARITHMETICS OR THEY DESYNC: `rowHeightFor`
 * (how far the row grows) AND `detailsTop` (where the level-2 panel starts).
 * Add it to one only and the details panel is drawn 6px INTO the strip.
 */
export const DAY_TOGGLE_ROW_GAP = 6;
/**
 * The row's expand/collapse animation — owner, 2026-08-22: *"can you do a
 * smooth expand rather than a harsh pop?"*
 *
 * 🔑 IT MUST BE APPLIED TO BOTH COLUMNS OR THEY DESYNC. The label column row
 * and the timeline row are SEPARATE elements that happen to share a height
 * (`rowHeightFor`). Animating one and not the other tears the row in half
 * mid-transition, which is a worse artefact than the pop this replaces.
 *
 * 🪤 `overflow-hidden` is part of the effect, not tidiness. The day strip is
 * positioned at `top: rowHeight` with its own 44px, so while the row is still
 * growing the strip would otherwise be drawn OUTSIDE it — i.e. it would pop
 * into view at full size and then the row would catch up, which is the exact
 * thing being fixed.
 *
 * ♿ `motion-reduce:transition-none` is REQUIRED, not polish. This is the first
 * motion in this component and the first `motion-reduce:` anywhere in
 * `client/src` — a user who has asked their OS for reduced motion gets the
 * instant change instead, which is the behaviour this replaced and is a
 * perfectly good outcome for them.
 */
export const ROW_EXPAND_TRANSITION =
  'overflow-hidden transition-[height] duration-200 ease-out motion-reduce:transition-none';

/* ═══════ WHERE THE LEVEL-2 DETAILS PANEL LIVES — read this first ═════════
 *
 * 🚨 THE PANEL IS **INLINE AT ITS ROW AND SPANS THE FULL CARD WIDTH — BOTH THE
 * MEMBER COLUMN AND THE TIMELINE.** Owner ruling, 2026-08-23 (the fourth time he
 * asked): *"can we make the expanding user details section full width so it uses
 * the expanded empty space in the 'Members' column? this would make it feel less
 * cramped and give more room for the summary."*
 *
 * 🔻 **THIS IS A SCROLL-MODEL CHANGE, AND IT DELETED FOUR MECHANISMS RATHER THAN
 * ADDING A FIFTH.** Read that first, because the deleted things are exactly what
 * a future edit will reach for again:
 *
 *   - `position: sticky` + `left: 0` on the panel — GONE, with
 *     `DETAILS_PANEL_STICKY_LEFT`.
 *   - `useMeasuredWidth` on the scrollport, to size the panel — GONE. The panel
 *     is `left-0 right-0` of a box that does not scroll sideways, i.e. 100%.
 *   - The *"no `overflow` ancestor between the panel and the scrollport"* rule —
 *     GONE. It existed ONLY because a sticky element resolves against its nearest
 *     scroll-container ancestor; with nothing sticky there is nothing to un-pin.
 *   - The manual `labels.scrollTop = timeline.scrollTop` sync, its `onScroll`
 *     handler, the per-instance `useId()` DOM id it needed, and the
 *     `syncLabelsId` argument to `useDragScroll` — ALL GONE. One box now scrolls
 *     vertically, so there is nothing to keep in step.
 *
 * ══════ THE SCROLL MODEL, WHICH IS THE WHOLE CHANGE ══════════════════
 *
 *   ┌ body scroller ─ `overflow-y-auto`, `relative`, maxHeight 600 + panel ─┐
 *   │ ┌ DATE BAND (48px) ─ `sticky top-0 z-30`, opaque, FIRST child ─────┐  │
 *   │ │ Member corner (280px)       │ clip pane `min-w-0 overflow-hidden`│  │
 *   │ │                             │   GanttDateAxis (follows timeline) │  │
 *   │ └─────────────────────────────────────────────────────────────────┘  │
 *   │ ┌ flex row ───────────────────────────────────────────────────────┐  │
 *   │ │ label column (fixed 280px)  │ timeline — `overflow-x-auto` ONLY │  │
 *   │ │   rows                      │   grid / today / bars             │  │
 *   │ └─────────────────────────────────────────────────────────────────┘  │
 *   │ ▸ details panel — `absolute left-0 right-0`, top = 48 + detailsTop   │
 *   └──────────────────────────────────────────────────────────────────────┘
 *
 * 📌 THE DATE BAND (owner ruling 2026-09-23, "option C"). The date header used to
 * be the timeline's first child, and a `sticky top-0` there is INERT: the
 * timeline is the header's nearest scroll container, and an `overflow-x-auto`
 * box computes its y to `auto` too, so the header pinned against a box that
 * never scrolls vertically. The header therefore moved OUT, into a band that is
 * the body scroller's first child — the box that DOES scroll vertically — with
 * the "Member" corner beside it. The band cannot scroll sideways with the bars
 * any more, so its clip pane FOLLOWS the timeline one way
 * (`hooks/useFollowScrollLeft`), a `deltaX` wheel on the band is forwarded to the
 * timeline, and a drag on the band pans the timeline (`useDragScroll`'s
 * `scrollTargetRef`). 🚫 Never `getElementById` here — both refs are local.
 *
 * 🔑 **RULE 1 — THE VERTICAL SCROLL BELONGS TO THE BOX THAT HOLDS BOTH COLUMNS.**
 * The timeline owns the HORIZONTAL axis and nothing else. That single move is
 * what makes the panel full width possible: it is a sibling of the flex row, in a
 * box whose width is the card's, so `left: 0; right: 0` IS the full width and no
 * measurement is involved. It is also what deletes the scroll sync — the label
 * column and the timeline are now inside one scroller, so they cannot desync.
 * 🪤 The timeline must keep `min-width: 0` behaviour as a flex item, which it gets
 * for free: a flex item whose `overflow` is not `visible` resolves `min-width:
 * auto` to 0. Give it `overflow-x-visible` and the 29,000px content pushes the
 * body scroller sideways and the panel's `right: 0` follows it off screen.
 * 🪤 `overflow-y-auto` alone makes the body scroller a scroll container on BOTH
 * axes (CSS computes a `visible` axis to `auto` when its partner is not
 * `visible`). That is harmless here and is why nothing needs `overflow-x-hidden`,
 * which would clip the panel's own shadowing and is not needed.
 *
 * 🔑 **RULE 2 — THE PANEL IS ABSOLUTE IN THE BODY SCROLLER, NOT IN THE TIMELINE.**
 * An absolutely positioned child of a scroll container SCROLLS WITH THE CONTENT
 * and is sized against the scroller's padding box — so `left-0 right-0` gives the
 * visible width, and the panel still travels vertically with its row. Nesting it
 * back inside the timeline puts it in the horizontally scrolling subtree, where it
 * would need the sticky machinery this change deleted; nesting it inside a ROW
 * clips it, because every row carries `overflow-hidden` from
 * `ROW_EXPAND_TRANSITION`.
 *
 * 🔑 **RULE 3 — ONE ARITHMETIC, NOW WITH THE HEADER IN IT.** `rowHeightFor` is
 * still the ONE height, read by the label column row, the timeline row,
 * `chartHeight`, the background grid and the today marker. `detailsTop` is still
 * the row's cumulative offset INSIDE the rows container. The panel now lives one
 * level further out, below the date band, so its `top` is
 * `headerHeight + detailsTop` — the band is exactly `headerHeight` IN FLOW
 * (sticky keeps its in-flow box), which is what makes the one term correct.
 * ⚠️ `DAY_TOGGLE_ROW_GAP` must stay in BOTH `rowHeightFor` and `detailsTop` or the
 * panel is drawn 6px inside the open day strip. That has not changed.
 *
 * 📐 **THE 600px CAP — UNCHANGED, AND STILL A DELIBERATE ANSWER.** The cap is
 * **RAISED BY EXACTLY THE OPEN PANEL'S HEIGHT**, so the chart keeps precisely the
 * 600px of ROW viewport it had and the panel gets its own space on top; the card
 * grows and the PAGE scrolls, which is what the crew table already does when a row
 * expands. `detailsHeight` is 0 whenever the panel is shut, so this is literally
 * `600px` for every user who never opens one. 🚫 The two alternatives were rejected
 * on stated grounds: DROPPING the cap makes a 150-crew chart ~6,300px tall for
 * everyone the moment any panel opens; giving the PANEL its own `overflow-y` puts a
 * third scroll axis on one surface, and a scrollbar inside a form inside a chart is
 * how a wage field gets missed.
 *
 * 🚧 `data-pan-skip` IS STILL ON THE PANEL, for a NEW reason. It used to opt out of
 * the timeline's pan; the panel is no longer in the timeline. It now opts out of
 * the BODY scroller's vertical pan (`useDragScroll(bodyScrollRef, { axis: 'y' })`),
 * without which a drag begun on the panel's padding would scroll the chart out from
 * under the form. Same attribute, different scroller — do not delete it as residue.
 *
 * 🚧 THE Z ORDER INSIDE THE CHART, lowest to highest: resting bars (5/10) →
 * today marker (z-20) → this panel (z-25) → a DRAGGING bar (`GANTT_DRAG_Z`,
 * 27) → the sticky date band (`GANTT_AXIS_Z`, z-30). The today marker is a
 * full-height dashed red line and would otherwise be drawn straight through the
 * wage fields. 🚨 The drag tier was 30 until 2026-09-23: a TIE with the band
 * that the bar, later in the DOM, won — measured in Chromium, the dragged
 * member's day cells painted over the month labels. Keep drag < band.
 *
 * 🪰 jsdom has NO LAYOUT, NO SCROLLING AND NO VIEWPORT, so it can see none of
 * this. §2 of `BudgetCostCodes.ganttSecondExpand.test.js` pins the CONTRACT that
 * produces it — the panel is OUTSIDE the horizontal scrollport, outside every row,
 * spans `left-0 right-0` with no measured width, and sits at
 * `headerHeight + detailsTop`. The geometry itself is measured in Chromium by
 * `scripts/measure-gantt-details-placement.js` (variant F). Whether it reads well
 * is the owner's eye.
 *
 * 🔻 THIS WAS `export const DETAILS_PANEL_PLACEMENT = 'full-width-in-vertical-
 * scroller'` UNTIL 2026-08-23, AND DELETING THE CONSTANT IS THE POINT OF THE
 * CHANGE, NOT A SIDE EFFECT. It had no importer and no test reader, and its
 * VALUE was never evaluated by anything: every one of its references — nine
 * comments below in this file, one in `client/src/components/budget/CLAUDE.md`
 * and one in `scripts/measure-gantt-details-placement.js` — is prose saying
 * "see `DETAILS_PANEL_PLACEMENT`", and not one of them reads the string. It
 * was a documentation anchor wearing a constant's clothes, and in a directory
 * whose stated convention is that *an export exists so it can be pinned* it read
 * as a pin somebody forgot to wire. The prose is what was load-bearing, so the
 * prose stays and the anchor is now this heading — the name still resolves to
 * exactly one definition site under grep, which is all those six comments ever
 * needed. 🚫 Do not "restore" it as a constant to satisfy a reference: a string
 * nothing reads is not a contract, and re-adding it would re-create an export
 * that no test can meaningfully pin.
 */

export const DAY_EDIT_DISABLED_TITLE = 'Switch to Week or Month view to edit days';
export const DAY_EDIT_NO_PERMISSION_TITLE = 'You do not have permission to edit worked days';

/** 🔀 Click-vs-drag on an EXPANDED bar, RESTORED 2026-10-05 (see the block
 * below): the pointer's LATCHED excursion radius, never "did the date change"
 * (a drag out and back to its origin moves no date and is still a drag). The
 * 2026-08-22 values, which worked: 5px mouse, 10px touch (a finger wobbles). */
export const CLICK_DRAG_THRESHOLD_PX = 5;
export const TOUCH_CLICK_DRAG_THRESHOLD_PX = 10;

/* ═══════════ A CLEAN CLICK ON AN EXPANDED BAR COLLAPSES IT — 2026-10-05 ═══════
 *
 * 🔀 REVERSED AGAIN, 2026-10-05. Owner, verbatim: *"clicking a gannt bar a
 * second time should close it"*. This reverses the 2026-08-23 ruling quoted
 * below (*"clicking on a gantt bar should NOT collapse it…"*) and restores the
 * 2026-08-22 discrimination: `handleDragEnd` reads a BODY gesture that never
 * left `CLICK_DRAG_THRESHOLD_PX` (touch: `TOUCH_…`) and moved no date as a
 * click and calls the SAME `onDayEditToggle` as the row/chevron (so a dirty
 * strip PROMPTS on the page). Any gesture past the radius is a drag: it moves
 * dates as before and never collapses. Edge handles never collapse. The
 * history below is kept as the record of both earlier rulings.
 *
 * ═══════════ (superseded) BAR CLICK NEVER COLLAPSES — owner reversal, 2026-08-23
 *
 * 🔄 THIS REVERSES A FEATURE BUILT EARLIER THE SAME DAY, AND THE REVERSAL IS
 * THE RECORD WORTH KEEPING HERE, NOT THE THING THAT WAS BUILT.
 *
 * What was asked for first, 2026-08-22, verbatim: *"how about a click on an
 * expanded gantt bar that is NOT dragged collapses the gantt whereas a click
 * and drag moves the gantt bar and does not collapse?"* It shipped: an
 * expanded bar's `handleDragEnd` classified the gesture as a click or a drag
 * by POINTER MOVEMENT (a latched excursion radius — 5px mouse, 10px touch —
 * because "the dates did not change" is wrong on a drag out and back to its
 * origin) and called `onDayEditToggle` when the gesture read as a click.
 *
 * What came back, 2026-08-23, after he used it, verbatim: *"clicking on a
 * gantt bar should NOT collapse it. only clicking on the Members name area on
 * the members column should collapse it."*
 *
 * ✅ THE RULE 08-23 → 10-05: **the timeline never COLLAPSES, in either state.**
 * A collapsed bar pans, and since 2026-10-05 its click EXPANDS (owner: *"like
 * the other gannt charts clicking the bar should expand it"* — see the
 * `data-pan-skip` block below); an expanded bar drags to move dates and a
 * click ALSO does nothing, matching its sibling rather than being a second
 * special case. Expansion and collapse both live on the label-column row and
 * the chevron alone — see `onDayEditToggle`'s call sites on those two
 * controls further down this file.
 *
 * 🗑️ THE MOVEMENT CLASSIFIER IS DELETED, NOT LEFT DISCONNECTED. It existed
 * for exactly one purpose — telling "a click that should collapse" apart from
 * "a drag that should not" — and with clicking removed from the bar entirely,
 * every pointer gesture on an expanded bar is a drag, so there is nothing
 * left to discriminate. Checked before deleting: `CLICK_DRAG_THRESHOLD_PX`,
 * `TOUCH_CLICK_DRAG_THRESHOLD_PX` and the `pointerGesture` latch had no
 * caller anywhere in `client/src` outside this file's own drag handlers and
 * their own test file (grepped, 2026-08-23). Leaving the threshold behind
 * with nothing left calling it would read as an unfinished feature to the
 * next person, who would "restore" the very collapse just removed — hence
 * deleting it whole rather than leaving it orphaned.
 *
 * 🪤 WHAT THIS DOES NOT TOUCH. The zero-movement short-circuit in
 * `handleDragMove` (a delta that rounds to zero days AND leaves the current
 * dates equal to the original ones returns before touching state) is
 * untouched and still what makes a stray click on an expanded bar write
 * nothing: `datesMoved` is false, so `handleDragEnd` returns without calling
 * `onMemberUpdate` either. That guard was never part of the click classifier
 * — it is what makes "click does nothing" SAFE rather than merely quiet, and
 * it survives this change intact.
 *
 * If click-to-collapse is ever asked for again, the discrimination approach
 * that worked — a latched movement radius, never a "did the date change"
 * test, because of the drag-out-and-back case — is preserved in git history
 * at this file's blame for 2026-08-22, and in
 * `client/src/components/budget/CrewGanttView.dayToggle.test.js`'s §10 as it
 * stood that day. Do not re-derive it from scratch.
 */

/* ═══════════ "GREY OUT THE OTHER ROWS" — owner ruling, 2026-08-22 ═════════
 *
 * *"can we also 50% out all the other bars when one is expanded please so it
 * obvious which we are editing."* Then, offered a background-tint alternative:
 * **"no I want the others faded or even grey scale"**. Shown five options
 * behind a temporary on-screen switcher (greyscale / fade / highlight-active /
 * grey+highlight / maximum) — decided: **"grey scale only decided please."**
 * The switcher and the other four options are deleted; this is the winner.
 *
 * 🔑 GREYSCALE, NOT A FADE, AND THE OWNER'S SECOND INSTINCT BEAT THE PROPOSAL
 * PUT TO HIM. `filter: grayscale(1)` spends SATURATION — a channel nothing in
 * this component uses for anything. `opacity` could not have carried it:
 *   • `opacity-40 pointer-events-none` on a crew TABLE row already means
 *     "another row has unsaved changes" (`components/InactiveBadge.js` forbids
 *     re-use in terms), and
 *   • inside the bar `opacity` already means WHETHER A DAY WAS WORKED
 *     (`WORKED_DAY_OPACITY` 1 / `FADED_DAY_OPACITY` 0.4).
 *
 * 📐 THE ARITHMETIC THAT DECIDED IT, because a 50% row fade multiplies with
 * the per-day fade already inside the bar (nested `opacity` composites, so a
 * child's 0.4 under a 0.5 parent renders at 0.20, NOT 0.4):
 *
 *   |                              | 50% fade | greyscale |
 *   |------------------------------|----------|-----------|
 *   | edited bar, worked day       |   1.00   |   1.00    |
 *   | edited bar, non-worked day   |   0.40   |   0.40    |
 *   | edited bar, OFF week         |   0.15   |   0.15    |
 *   | OTHER bar, worked day        |   0.50   |   1.00    |
 *   | OTHER bar, non-worked day    |   0.20   |   0.40    |
 *   | OTHER bar, OFF week          |   0.075  |   0.15    |
 *
 * The within-row ratio survives a fade (0.50 : 0.20 is still 2.5 : 1), so the
 * often-stated "50% flattens worked vs non-worked to 0.5 vs 0.4" is wrong —
 * but TWO REAL LOSSES are not. An unedited WORKED day (0.50) lands 0.1 from an
 * edited NON-WORKED day (0.40), so the one distinction the fade exists to make
 * collides with the one the bar already makes; and an unedited OFF week falls
 * to **0.075 alpha — effectively invisible**. No value of the fade escapes
 * both: raise it and it stops reading as de-emphasis, lower it and the off
 * weeks vanish faster. Greyscale leaves the whole column unchanged.
 *
 * 🔒 THE BAR/TIMELINE ROW ONLY — NOT THE LABEL COLUMN, and that is a decision
 * rather than where the class happened to land. The label column's text is
 * already achromatic, so greying it would change exactly ONE thing: the GRADE
 * DOT beside the name. Grade has three channels (bar colour + legend, the dot,
 * the row `title`); the bar is being desaturated here by design, so greying
 * the dot as well would leave a hover-only `title` as the sole surviving
 * channel on every unedited row. The dot stays coloured on purpose.
 *
 * ✅ CHECKED, NOT ASSUMED:
 *   • OFF WEEKS survive. Greyscale does not touch `opacity`, so an off week is
 *     exactly as distinguishable from a partly-worked one as it is today minus
 *     hue — and its discriminator was never hue, it is `border-dashed`, which
 *     is a SHAPE and comes through untouched.
 *   • PREMIUM MARKS are near-achromatic already and barely move: the fill is
 *     `rgba(17, 24, 39, …)` (gray-900, a slight blue cast), whose luminance is
 *     0.2126·17 + 0.7152·24 + 0.0722·39 ≈ 23.6, i.e. `#111827` → `#181818`.
 *     Not "untouched" — a shift of about 7/255 on one channel, invisible in
 *     practice. The white digit stays white. Their 12px/24px HEIGHT step, the
 *     channel the source docblock says always works, is unaffected.
 *   • DARK MODE needs no pair: a filter desaturates whatever colour is there,
 *     so it is theme-independent — one of the few treatments in this file that
 *     needs no `dark:` variant and no cascade check against `index.css`.
 *   • STACKING: `filter` makes an element a stacking context, which would trap
 *     a `z-30` dragging bar inside its own row. Harmless here BY CONSTRUCTION —
 *     the only draggable bar is the EXPANDED member's, and that row is never
 *     greyed. Do not move this onto a container that holds the expanded row.
 *
 * 🪤 jsdom applies no filters and renders nothing, so a test can assert this
 * style is SET and no more. Whether it reads as obvious is a browser check,
 * and the owner has already made it.
 */

/**
 * The inactive-row filter — GREYSCALE, the owner's ruling. Applied to every
 * OTHER member's TIMELINE row while one is expanded (never to the row being
 * edited, and never to the label column — see above); never expressed as
 * `opacity`, which already carries two meanings in this component (see the
 * arithmetic above). No `dark:` pair needed — a filter desaturates whatever
 * colour is already there.
 */
const INACTIVE_ROW_FILTER = 'grayscale(1)';

/* ═══════════ THE LABEL COLUMN'S NAME + PRICE — owner ruling, 2026-08-22 ═══
 *
 * *"remove the person's name and total price from the gantt bar and put the
 * total price after the person's name in the left column"*, then, on the
 * width: *"we can make this column a little wide to include the price. Maybe
 * truncate the name of the person slightly if required"*, then the rule
 * itself: **"truncate names >15 characters"**.
 *
 * 🔑 THE ELLIPSIS COUNTS WITHIN THE 15, AND THAT IS A DECISION — STATE IT
 * RATHER THAN RE-DERIVING IT. A truncated name renders 14 characters plus `…`,
 * so **the rendered string is never longer than 15 characters**, which is the
 * strictest reading of the owner's number and the only one that cannot be
 * argued two ways later. A name of EXACTLY 15 is not truncated.
 *
 * 🪤 CHARACTERS, NOT BYTES, AND NOT `String#slice` EITHER. Crew names here
 * carry accents and apostrophes; `[...name]` iterates CODE POINTS, so a name
 * with an astral character or a precomposed accent counts and cuts correctly
 * where `.length`/`.slice` would split a surrogate pair into two half-characters
 * and render a replacement glyph. ⚠️ Declared limit: a COMBINING mark is its
 * own code point, so `e` + `◌́` counts as two — rare in this data and not worth
 * an Intl.Segmenter dependency, but it is a real edge and it is written down
 * rather than left to be discovered.
 *
 * 🚨 IT IS A CHARACTER RULE, SO IT IS IMPLEMENTED AS ONE. The CSS `truncate`
 * class stays on the span as a belt-and-braces backstop for an unexpectedly
 * narrow viewport, but this function is what actually decides — CSS clipping is
 * invisible to jsdom, which has no layout, so a rule expressed only in CSS
 * could not be tested at all.
 */
export const CREW_NAME_MAX_CHARS = 15;

/* ═══════ THE MEMBER ROW'S HOVER / FOCUS AFFORDANCE — 2026-08-22 ═══════════
 *
 * Owner: *"also add in a hover effect over the name member column"*, and
 * *"use the same hover colour if possible from other site areas."*
 *
 * 🔑 IT IS THE HOUSE **RULE** THAT IS REUSED, NOT THE HOUSE **CLASS**, AND THE
 * DIFFERENCE IS THE WHOLE ENTRY. `client/src/components/ui/CLAUDE.md:111`
 * gives the Expandable-Table-Row hover as `hover:bg-gray-50
 * dark:hover:bg-gray-700/30` — which is exactly what this row now is, so it is
 * the right rule to take. But that pair presumes a row resting on **white**,
 * and the Gantt's label column is `bg-gray-50 dark:bg-gray-900`
 * (`labelWidth` container, a few hundred lines below). **`bg-gray-50` hovering
 * to `bg-gray-50` is `#f9fafb` on `#f9fafb` — a hover that renders nothing at
 * all in light mode.** Copying the documented class here would have shipped a
 * dead affordance while looking, in a diff, exactly like following the rule.
 *
 * 📏 SO THE LIGHT HALF STEPS ONE SHADE, WHICH IS WHAT THE RULE ACTUALLY SAYS.
 * The house pattern is *one step darker than the surface the row rests on*
 * (white → `gray-50` hover → `gray-100` when expanded). This row rests on
 * `gray-50`, so its step is `gray-100`: `#f3f4f6` on `#f9fafb`. The DARK half
 * is taken from `:111` verbatim.
 *
 * 🪤 AND THE CASCADE WAS CHECKED, NOT ASSUMED — `client/src/index.css` carries
 * `html.dark` `!important` background overrides that make some `dark:`
 * variants inert. Read at `index.css:90-105`, the overridden selectors are
 * `.bg-gray-100`, `.bg-gray-50`, `.bg-gray-50\/50` and `.bg-white` — **the
 * BARE utilities only. There is no `html.dark .hover\:bg-*` rule anywhere in
 * that file**, so both halves below resolve normally:
 *   light  `hover:bg-gray-100`         → `#f3f4f6` over `#f9fafb`
 *   dark   `dark:hover:bg-gray-700/30` → `gray-700` at 30% over `#111827`
 *                                        (the column's `dark:bg-gray-900`,
 *                                        which is NOT in the override table)
 *                                        ≈ `#1c2434`
 * ⚠️ Both are arithmetic over the composited values, not a Chromium
 * measurement — jsdom has no stylesheet. Whether the step READS as an
 * affordance is a browser check and the owner's to judge; what is asserted
 * here is only that the light half is no longer identical to its own
 * background, which was the actual defect.
 *
 * ♿ `focus-within:` IS NOT DECORATION — IT IS THE REQUIRED SECOND CHANNEL.
 * A hover-only affordance does not exist for a keyboard user or on touch. The
 * row's real control is the chevron `<button>` beside it plus the row-surface
 * `<button>`, both genuine tab stops with their own focus rings; putting the
 * SAME background step on `focus-within` means tabbing to either one lights
 * the whole row exactly as hovering does, so the two channels cannot drift.
 *
 * 🚫 NOT `opacity-*`. That channel already carries two meanings in this
 * directory (the crew table's dirty guard, and worked-vs-not inside the bar)
 * and may not take a third — see the *taken idioms* section of
 * `client/src/components/budget/CLAUDE.md`.
 *
 * 🪤 WRITTEN AS ONE COMPLETE LITERAL, NEVER ASSEMBLED. Tailwind's scanner
 * reads source TEXT, so a class built at runtime (`` `hover:${shade}` ``) is
 * never compiled and the hover silently does nothing in a production build —
 * the same reason `constants/stickyTable.js` ships two literal axis strings
 * rather than a builder.
 */
export const ROW_EXPAND_HOVER =
  'hover:bg-gray-100 dark:hover:bg-gray-700/30 focus-within:bg-gray-100 dark:focus-within:bg-gray-700/30';

export function truncateCrewName(name, max = CREW_NAME_MAX_CHARS) {
  const chars = [...String(name ?? '')];
  if (chars.length <= max) return chars.join('');
  return `${chars.slice(0, max - 1).join('')}…`;
}

/** A week segment's worked share: the day count when the member has day data,
 *  else the allocation fraction. `0` IS an OFF week — read by the bar's render
 *  and by the tooltip's `0-day weeks:` line (`Off weeks:` until 2026-10-19),
 *  so the two cannot disagree. */
function segmentShade(seg) {
  return seg.workedDays === null || seg.workedDays === undefined
    ? seg.allocation
    : Math.min(seg.workedDays, 5) / 5;
}

/**
 * Generate week segments for a member's bar.
 * Each segment has a position relative to the bar start and an allocation value.
 */
function generateWeekSegments(member, timelineStart, dayWidth, resolvedWorkedDates) {
  const from = parseDate(member.start_date);
  const to = parseDate(member.end_date);
  if (!from || !to) return [];

  // Build override lookup from member.overrides
  const overrideMap = {};
  if (member.overrides) {
    for (const o of member.overrides) {
      overrideMap[o.week_start] = o.allocation;
    }
  }

  const workedSet = buildWorkedDateSet(member, resolvedWorkedDates); // null => Mon–Fri default

  const barStartDay = daysDiff(timelineStart, from);
  const segments = [];

  // `end_date` is the member's LAST WORKED DAY (owner ruling, 2026-08-24 —
  // it was exclusive before that). The internal machinery stays exclusive;
  // `drawnEnd` is the one conversion point: one day past the last worked day.
  const drawnEnd = addDays(to, 1);

  // Find the Monday on or before start_date
  let current = new Date(from);
  const dow = current.getDay();
  if (dow !== 1) {
    current.setDate(current.getDate() - ((dow + 6) % 7));
  }

  while (current < drawnEnd) {
    const weekKey = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;

    // Clamp segment to member's actual date range (the day ON end_date is
    // worked and drawn; `drawnEnd` is already one past it)
    const segStart = current < from ? from : current;
    const nextMonday = addDays(current, 7);
    const segEnd = nextMonday > drawnEnd ? drawnEnd : nextMonday;

    const segStartDay = daysDiff(timelineStart, segStart);
    const segDuration = Math.max(daysDiff(segStart, segEnd), 1);

    // Count weekdays (Mon-Fri) — through end_date INCLUSIVE (segEnd is the
    // exclusive form of the inclusive bound)
    let weekdays = 0;
    const wd = new Date(segStart);
    const segEndTime = segEnd.getTime();
    while (wd.getTime() < segEndTime) {
      const d = wd.getDay();
      if (d >= 1 && d <= 5) weekdays++;
      wd.setDate(wd.getDate() + 1);
    }

    const allocation = weekKey in overrideMap ? overrideMap[weekKey] : (member.default_allocation ?? 1);

    // ─── Worked days in this week — 6th/7th-day premium wage feature ──────
    //
    // 🔑 THE FADE IS NOW DRIVEN BY THE DAY COUNT INSTEAD OF THE ALLOCATION
    // FRACTION. Owner: "The current faded look for part weeks works well I
    // would like to keep it." SAME LOOK, HONEST INPUT — the bar is telling
    // you how much of the week is worked, and the day rows are what the
    // labour is actually costed from (`server/helpers/crewDays.js`).
    //
    // 🪤 COUNTED OVER THE WHOLE Mon–Sun WEEK, NOT THE CLAMPED SEGMENT. The
    // segment above is clipped to the member's real start/end dates, but
    // `allocation` never was, and neither is the boot backfill (which lays
    // down a full Monday-anchored week deliberately, because clamping would
    // move the cost). Counting over the clamped span would fade the first and
    // last weeks of every mid-week engagement differently from the number
    // they are charged for.
    //
    // 🟢 `workedDays` is NULL when the member has NO day rows at all — a
    // newly created member, or one whose rows were never backfilled. That is
    // the same explicitly-named case `resolveWorkedDays` keeps on the server,
    // and it means "fall back to the fraction", never "zero days". Do not
    // fold it into a 0.
    //
    // 🔀 COUNTED FROM `workedSet`, NOT FROM `member.days`, SINCE 2026-08-22.
    // Equivalent when the set was built from `member.days` (it is the same
    // dates), and REQUIRED once a member's day strip is open: `member.days` is
    // the STORED schedule, so counting it there would leave `isOff` — and
    // therefore the dashed off-week treatment — describing a week the user has
    // just emptied or refilled. One set, one count.
    let workedDays = null;
    if (workedSet !== null) {
      workedDays = 0;
      for (let i = 0; i < 7; i++) {
        if (workedSet.has(toDateStr(addDays(current, i)))) workedDays++;
      }
    }

    // ─── Per-day cells, for the per-day fade (owner ruling above) ─────────
    // Drawn only over the CLAMPED segment (segStart..segEnd) — exactly the
    // pixels the bar actually covers; `workedDays` above stays whole-week
    // deliberately (see the block comment at `buildWorkedDateSet`), this does
    // not. Each cell's `left` is in the same coordinate space as `seg.left`.
    const dayCells = [];
    const dCursor = new Date(segStart);
    while (dCursor.getTime() < segEndTime) {
      const dateStr = toDateStr(dCursor);
      const dow = dCursor.getDay();
      dayCells.push({
        date: dateStr,
        worked: isDayWorked(workedSet, dateStr, dow),
        // 🪤 MONDAY-INDEXED (0 = Monday), NOT `Date#getDay()`'s Sunday-first
        // value, because `DAY_INITIALS` / `DAY_NAMES` / `daysForPreset` /
        // `dateForWeekDay` are all Monday-first — every week key in this
        // feature IS a Monday. Storing the raw `dow` here and converting at the
        // render site would be a second place to get the offset wrong.
        dayIndex: (dow + 6) % 7,
        left: (daysDiff(timelineStart, dCursor) - barStartDay) * dayWidth,
        width: dayWidth,
      });
      dCursor.setDate(dCursor.getDate() + 1);
    }

    segments.push({
      weekKey,
      left: (segStartDay - barStartDay) * dayWidth,
      width: segDuration * dayWidth,
      allocation,
      workedDays,
      days: segDuration,
      weekdays,
      dayCells,
    });

    current.setDate(current.getDate() + 7);
  }

  return segments;
}

/* ═════════════════ THE 6th / 7th-DAY PREMIUM DAY MARKS ═════════════════
 *
 * Stage 3, Task 7 Step 7 of the 6th/7th-day premium wage feature. Owner:
 * **"The Gantt highlights 6th and 7th days with increasing boldness."**
 *
 * These marks say WHICH days are premium days. They say nothing about what a
 * premium day COSTS — that is `server/helpers/crewCost.js`'s job and it is
 * wired separately. Nothing in this block reads or derives money.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔑 RUN POSITIONS ARE COMPUTED OVER THE WHOLE ENGAGEMENT, NEVER PER WEEK
 * ─────────────────────────────────────────────────────────────────────────
 * `runPositions` is called ONCE per member on `member.days` in full, BEFORE
 * anything is sliced into week segments. That is not a performance choice, it
 * is the rule: a run that starts on a Friday reaches day 6 on the FOLLOWING
 * WEDNESDAY. Counting inside `generateWeekSegments` would restart the run at
 * every Monday and silently under-mark every run that crosses a week
 * boundary — a failure that looks like "some premium days are missing", which
 * is indistinguishable from correct output unless you count by hand. §3 of
 * `CrewGanttView.premiumDays.test.js` pins exactly that case.
 *
 * 🪤 Days OUTSIDE the drawn bar still count toward the run and are then
 * dropped from the drawing. A day row the day before `start_date` is part of
 * the run whether or not there is a pixel for it.
 *
 * 🚫 THE FADE IS NOT TOUCHED. `opacity` on this page already carries "how
 * full is this week", and `opacity-40 pointer-events-none` on
 * `pages/BudgetCostCodes.js` already means "another row has unsaved changes"
 * (`components/InactiveBadge.js` forbids reusing that idiom). Loading a THIRD
 * meaning onto opacity was available and is refused. Owner: *"The current
 * faded look for part weeks works well I would like to keep it."* The OFF
 * treatment (dashed border at `opacity: 0.15`) is likewise untouched.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE SHAPE — AND WHAT IT LOOKS LIKE AT EACH OF THE THREE ZOOMS
 * ─────────────────────────────────────────────────────────────────────────
 * `DAY_WIDTHS = { week: 40, month: 16, quarter: 5 }` (`utils/ganttDates`, read
 * through `hooks/useGanttZoom` since 2026-10-13), and `zoom` defaults to
 * **`'month'`** — so **16px per day is the case to get RIGHT**, 40px is the
 * comfortable case, and 5px is the degradation case.
 *
 * A mark is a solid block spanning the full day column, anchored to the
 * BOTTOM of the bar (`barHeight = 30`, and the bar is `overflow-hidden` so
 * nothing may overhang it). **Its HEIGHT is the boldness signal:**
 *
 *     standard day   no mark                    0px of 30
 *     6th day        PREMIUM_MARK_HEIGHTS[6] = 12px  (40% of the bar)
 *     7th day        PREMIUM_MARK_HEIGHTS[7] = 24px  (80% of the bar)
 *
 * 🔑 **HEIGHT IS THE DISCRIMINATOR BECAUSE HEIGHT DOES NOT SCALE WITH ZOOM.**
 * 12px vs 24px is the same 12px difference at 40, 16 and 5 pixels per day, so
 * the 6th and the 7th stay distinguishable from each other — and both from a
 * standard day — at every zoom. Anything carried on the horizontal axis (a
 * glyph, hatching, a double stripe, a corner mark) would not.
 *
 * On top of that, a literal **ordinal digit** — `6` or `7`, white on the
 * mark — is drawn when there is room for it:
 *
 *   ZOOM      day    what a premium day looks like
 *   week      40px   a 40×12 / 40×24 block with a clearly legible 6 or 7.
 *                    Two adjacent premium days read as an obvious step up.
 *   month     16px   THE DEFAULT AND THE PRIMARY TARGET. A 16×12 / 16×24
 *                    block, digit still legible (9px glyph in a 10px
 *                    interior). The height step is the dominant read; the
 *                    digit confirms it.
 *   quarter    5px   🔻 DEGRADED, DELIBERATELY. The digit is DROPPED
 *                    (`PREMIUM_MARK_DIGIT_MIN_DAY_WIDTH = 16`) — a 9px glyph
 *                    in a 5px column is illegible, and shipping it would be
 *                    shipping a distinction the pixels cannot carry. What
 *                    remains is two 5px-wide blocks of different heights.
 *                    You can still see THAT a day is premium and WHICH of
 *                    the two it is; you cannot read the date off the chart.
 *
 * 📏 WHAT ELSE DEGRADES AT QUARTER ZOOM, STATED PLAINLY:
 *   - **The hover target.** A mark is 5×12 / 5×24 px, a poor mouse target.
 *     Mitigated, not denied: `premiumDaysSummary()` puts the COUNTS on the
 *     bar's own hover tooltip, which is the whole bar and easy to hit at any
 *     zoom.
 *   - **Which calendar day a mark is.** The date is in the mark's
 *     `title`/`sr-only` string, and the day grid on `BudgetCostCodes.js` is
 *     where days are actually read and edited.
 *   - 🚫 What does NOT degrade is the 6-vs-7 distinction itself. If a future
 *     change moves the boldness onto width, opacity or a glyph, it WILL, and
 *     §5 of the test file is what goes red.
 *
 * ⚠️ A FULL-HEIGHT (30px) treatment for the 7th day was considered and
 * REJECTED at 24px: full height completely replaces the grade colour for that
 * day, and the fade — which the owner explicitly asked to keep — is read from
 * that colour. 80% is the most boldness available while a strip of the bar
 * still shows what it was saying. A judgement call, recorded as one.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ♿ THE COLOUR — MEASURED, AND WHY IT IS TWO TONES AND NOT ONE
 * ─────────────────────────────────────────────────────────────────────────
 * These marks are the SOLE CARRIER of "this day is a premium day", so the
 * root `CLAUDE.md` sole-carrier rule applies in full: WCAG 1.4.11's 3:1
 * non-text minimum against the ACTUAL background, measured, plus a non-colour
 * channel.
 *
 * 🪤 **"The background" is not one colour.** A bar is the GRADE's own hex
 * (`constants/budget.js#GRADE_COLORS`, eight of them) at an opacity that
 * varies with how full the week is (`0.4 + shade * 0.6`), composited over the
 * card — `#ffffff` in light and `#1e293b` in dark, the latter being
 * `index.css:103`'s `html.dark .bg-white { … !important }` beating the
 * element's own `dark:bg-gray-800`. Weekend columns add a further tint
 * (`bg-blue-50/50` light, `dark:bg-blue-900/15` dark) and **premium days are
 * usually Saturdays and Sundays**, so the weekend backgrounds are the ones
 * that matter most here, not an afterthought.
 *
 * 📏 96 combinations measured (8 grades × 3 opacities × 4 backgrounds; the
 * opacities are α = 0.52 / 0.64 / 1.00, i.e. a week with 1, 2 and 5+ worked
 * days — a week containing a premium day always has at least one worked day,
 * so α never drops below 0.52). WCAG 2.x relative luminance:
 *
 *   `#111827` (gray-900) ALONE — worst case **2.23:1, FAILS**
 *      (indigo-500 at α=0.52 on the dark card; 8 of 96 fail)
 *   `#ffffff` ALONE — worst case **1.49:1, FAILS**
 *      (amber-500 at α=0.52 on white; most light-theme combinations fail)
 *
 * 🔑 **NEITHER TONE PASSES ALONE, AND NO SINGLE TONE CAN**, because the bar
 * ranges from near-white (a faint grade over white) to a saturated mid-tone
 * inside one component.
 *
 * 🔻 **RE-MEASURED 2026-08-22 — THE FILL DROPPED TO 50% OPACITY, OWNER
 * REQUEST, AND THE OLD PROOF DOES NOT SURVIVE IT UNCHANGED.** Owner: *"would
 * rather the bars were 50% opacity so we still see some of the gantt bar
 * colour coming through."* Diluting the fill toward the bar's own colour
 * REDUCES the fill's own contribution — re-measured (same 96-combination
 * method, `rgba(17,24,39,0.5)` composited over each of the same 96 bar
 * colours): **worst case of `max(fill, keyline)` FALLS to 2.60:1, and 42 of
 * 96 combinations now FAIL 3:1** (worst: indigo-500 at α=0.64 on the
 * light-weekend background). **A 50%-opaque fill plus the unchanged 1px
 * keyline alone can no longer clear 3:1 against every background — say so
 * plainly, per the brief, rather than shipping a distinction the pixels
 * cannot carry.**
 *
 * 🟢 **THE FIX: a SECOND inset ring, at the fill's FULL opacity, sits just
 * inside the (unchanged) white keyline.** The translucent 50%-opacity fill
 * now paints only the INTERIOR of the mark, beyond both rings — the part that
 * shows the bar colour through, exactly as asked — while the mark's own
 * BOUNDARY carries the same two full-strength tones the original proof
 * measured. Both rings are drawn with `box-shadow: inset`, never a change to
 * `backgroundColor`'s alpha, so they are immune to the fill's opacity:
 *
 *   `boxShadow: inset 0 0 0 1px ${KEYLINE}, inset 0 0 0 2px ${FILL}`
 *
 * — a 1px white band at the very edge, a 1px `#111827` band just inside it,
 * then the 50%-opacity fill from 2px inward. Because both ring colours are
 * the SAME two opaque tones as before, **the boundary's worst case is
 * UNCHANGED at 4.21:1** — the original 96-combination proof applies to it
 * verbatim, including the "clears 3:1 for any background luminance" argument
 * (contrast-to-black ≥3:1 whenever L≥0.1, contrast-to-white whenever L≤0.3,
 * every L satisfies one). **This is the smallest change that restores 3:1
 * while keeping the fill at the requested 50%**: no new colour, no change to
 * either existing tone, the same inset idiom, additive rather than a
 * redesign.
 *
 * ⚠️ **What this does NOT restore, stated so it is not misread as fixed: the
 * INTERIOR — the majority of the mark's area, beyond the 2px ring — carries
 * only the diluted fill, and that portion alone still fails 3:1 in the same
 * 42/96 combinations.** That is accepted, not overlooked: the interior is not
 * the sole carrier of "this is a premium day" any more, the boundary ring is,
 * exactly as an outlined badge or a ghost button relies on its edge rather
 * than its fill. The ordinal digit sits over the diluted interior, not the
 * ring — its own contrast against the 50%-fill is **4.615:1 worst case**
 * (same 96-combination sweep, digit colour = keyline vs the diluted fill),
 * which clears WCAG 1.4.3's 4.5:1 normal-text minimum, though by a narrow
 * margin, and the digit is `aria-hidden` decoration reinforcing the height
 * distinction, not its sole channel.
 *
 *   worst case of max(fill, keyline) over all 96, RING PORTION = **4.21:1**
 *      (green-500 at α=0.64 on the dark card — UNCHANGED from the original
 *      opaque design, because the ring is still fully opaque)
 *   fill(50%) vs keyline, INTERIOR/digit = **4.615:1** worst case
 *      (was 17.74:1 opaque; still clears 4.5:1 normal text, narrowly)
 *
 * 🟢 That is a PROOF for the ring, not merely a table: contrast-to-black
 * clears 3:1 whenever the background luminance L ≥ 0.1, contrast-to-white
 * clears it whenever L ≤ 0.3, and every possible L satisfies at least one. A
 * ninth grade colour added later cannot break the ring. **Re-measure the ring
 * anyway if either FILL or KEYLINE changes, and re-measure the interior/digit
 * pair if the fill's OPACITY changes** — the proof is about the two colours
 * at full strength, not about any diluted blend.
 *
 * 🪤 Both rings are INSET (`box-shadow: inset`), never outset: at quarter
 * zoom an outset 1px ring would spill into each neighbouring day and two
 * adjacent premium days would fuse into one blob.
 *
 * ♿ NON-COLOUR CHANNELS, four of them:
 *   - SHAPE/SIZE. The 6th and the 7th are the SAME colour — only the height
 *     differs — so colour carries none of the 6-vs-7 distinction.
 *   - the ordinal DIGIT, wherever there is room for it.
 *   - `title` + an `sr-only` span, both fed from ONE exported string
 *     (`premiumDayTitle`) — the `components/InactiveBadge.js` device that
 *     `CrewDayGrid.js#unpricedPremiumTitle` already uses on this feature. One
 *     string, so the tooltip and the screen reader cannot drift.
 *   - the counts on the bar's own hover tooltip (`premiumDaysSummary`).
 * ⚠️ The bar has NO native `title` of its own (it uses the custom `showTooltip`
 * panel), so a `title` on a mark shadows nothing — the trap `DueIndicator` and
 * `LeadTimeRisk` both hit. The two are COMPLEMENTARY: the panel says how many
 * premium days the member has, the mark says which day this one is.
 *
 * 🪤 jsdom HAS NO STYLESHEET, LAYOUT ENGINE OR VIEWPORT. Everything below is
 * asserted as classes, inline styles and structure. **Nothing in the test
 * file says any of this LOOKS right** — the contrast figures come from the
 * arithmetic above, and the legibility of a 9px digit at 16px per day is a
 * BROWSER check.
 */

/** Bottom-anchored mark height in px, by run position. `barHeight` is 30. */
export const PREMIUM_MARK_HEIGHTS = { 6: 12, 7: 24 };
/**
 * The two tones. Measured above — do not change either without re-measuring
 * BOTH the ring (full-opacity pair) and the interior/digit (fill diluted by
 * `PREMIUM_MARK_FILL_OPACITY`).
 */
export const PREMIUM_MARK_FILL = '#111827';
export const PREMIUM_MARK_KEYLINE = '#ffffff';
/**
 * `PREMIUM_MARK_FILL` as `r, g, b`, so the interior can be expressed as an
 * rgba() rather than relying on the CSS `opacity` property — `opacity` on the
 * element would ALSO fade the box-shadow rings and the digit, undoing the
 * exact thing the two-tone ring exists to preserve. Derived from
 * `PREMIUM_MARK_FILL` by hand; keep the two in step if either changes.
 */
export const PREMIUM_MARK_FILL_RGB = '17, 24, 39';
/**
 * Owner request, 2026-08-22: "would rather the bars were 50% opacity so we
 * still see some of the gantt bar colour coming through." Applies to the
 * INTERIOR fill only (see the block comment above) — the two boundary rings
 * stay at full strength regardless of this value.
 */
export const PREMIUM_MARK_FILL_OPACITY = 0.5;
/**
 * Below this many pixels per day the ordinal digit is dropped. 16 is `month`
 * zoom, the default; `quarter` is 5 and gets height alone. Same width-gating
 * idiom as the segment labels' `seg.width >= 35` a few lines below.
 */
export const PREMIUM_MARK_DIGIT_MIN_DAY_WIDTH = 16;

/**
 * The ONE wording. Feeds the `title` AND the `sr-only` copy so they cannot
 * drift — `InactiveBadge.js`'s device.
 */
export function premiumDayTitle(position, dateStr) {
  const ordinal = position === 7 ? '7th' : '6th';
  const when = dateStr ? formatDateShort(dateStr) : '';
  return `${when ? `${when} — ` : ''}${ordinal} consecutive day worked. Premium day.`;
}

/**
 * Every premium (run position 6 or 7) day of a member's WHOLE engagement, in
 * date order. Not sliced by week — see the block comment.
 */
function premiumDatesOf(member, resolvedWorkedDates) {
  // 🚨 THE OPEN EDITOR'S SET WINS HERE TOO, AND IT HAS TO. `runPositions`
  // is what decides which days are 6th/7th days, and it was reading
  // `member.days` — the STORED schedule. With the day toggles open that is the
  // one thing on screen that is NOT what the user is editing: tick a Saturday
  // and the fade, the toggles and the payload all move while the premium marks
  // stay on the old run, so the bar would claim the 6th day is somewhere it is
  // about to stop being. One set, one answer, everywhere.
  // 🪤 `runPositions` (the CLIENT mirror) SORTS its input, so an unordered
  // Set is safe here where the server's contract would throw.
  const rows = resolvedWorkedDates instanceof Set
    ? [...resolvedWorkedDates].map((work_date) => ({ work_date }))
    : (Array.isArray(member?.days) ? member.days : []);
  if (rows.length === 0) return [];
  const positions = runPositions(rows.map((r) => r && r.work_date));
  const out = [];
  for (const [date, position] of positions) {
    if (isPremiumPosition(position)) out.push({ date, position });
  }
  return out;
}

/**
 * `{ six, seven }` over the whole engagement. Counts every premium day the
 * member has, including any falling outside the drawn bar — the tooltip is
 * about the ENGAGEMENT, not about what happens to be on screen.
 */
export function countPremiumDays(member) {
  const counts = { six: 0, seven: 0 };
  for (const { position } of premiumDatesOf(member)) {
    if (position === 7) counts.seven += 1;
    else counts.six += 1;
  }
  return counts;
}

/** The tooltip line, or `null` when there is nothing to say. */
export function premiumDaysSummary(member) {
  const { six, seven } = countPremiumDays(member);
  if (!six && !seven) return null;
  const parts = [];
  if (six) parts.push(`${six} × 6th`);
  if (seven) parts.push(`${seven} × 7th`);
  return parts.join(' · ');
}

/**
 * The marks to DRAW, positioned relative to the bar's left edge.
 *
 * 🪤 `end_date` is INCLUSIVE — the member's last worked day (owner ruling,
 * 2026-08-24), exactly as `generateWeekSegments` and `getBarStyle` treat it.
 * A premium day ON `end_date` is part of the bar and is drawn; a day past it
 * is not.
 */
export function computePremiumDayMarks(member, timelineStart, dayWidth, resolvedWorkedDates) {
  const from = parseDate(member?.start_date);
  const to = parseDate(member?.end_date);
  if (!from || !to || !timelineStart) return [];
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return [];

  const barStartDay = daysDiff(timelineStart, from);
  const marks = [];
  for (const { date, position } of premiumDatesOf(member, resolvedWorkedDates)) {
    const d = parseDate(date);
    if (!d || Number.isNaN(d.getTime())) continue;
    if (d < from || d > to) continue;
    marks.push({
      date,
      position,
      left: (daysDiff(timelineStart, d) - barStartDay) * dayWidth,
      width: dayWidth,
      height: PREMIUM_MARK_HEIGHTS[position],
    });
  }
  return marks;
}

/** One premium day's mark. Rendered inside the bar. */
function PremiumDayMark({ mark, barHeight, dayWidth }) {
  const title = premiumDayTitle(mark.position, mark.date);
  const height = Math.min(mark.height, barHeight);
  const showDigit = dayWidth >= PREMIUM_MARK_DIGIT_MIN_DAY_WIDTH;
  return (
    <span
      title={title}
      data-testid="gantt-premium-day"
      data-premium-position={mark.position}
      data-premium-date={mark.date}
      className="absolute flex items-center justify-center select-none"
      style={{
        left: mark.left,
        width: mark.width,
        height,
        top: barHeight - height,
        // 50%-opacity interior (owner request, re-measured above) — an rgba()
        // so ONLY the fill dilutes, never the box-shadow rings or the digit.
        backgroundColor: `rgba(${PREMIUM_MARK_FILL_RGB}, ${PREMIUM_MARK_FILL_OPACITY})`,
        // 🔻 OWNER REQUEST 2026-08-22, and it is a KNOWN CONTRAST REGRESSION
        // shipped deliberately to be looked at: *"50% looks good. but remove
        // the white border on the 6th and 7th day. I want to see how that
        // looks."* The white keyline is GONE; only the opaque fill ring
        // remains. `#111827` alone is worst-case **2.23:1 and FAILS 3:1**
        // against the lighter grade colours (measured, same 96-combination
        // sweep as above) — the pair cleared it precisely BECAUSE one member
        // of the pair was white. This is an evaluation build, not a verdict:
        // if it stays, the sole-carrier rule needs an explicit ruling, and the
        // 6-vs-7 HEIGHT step (12px/24px) is then the only channel that always
        // works. Restore by putting `inset 0 0 0 1px ${PREMIUM_MARK_KEYLINE},`
        // back at the front of this list.
        boxShadow: `inset 0 0 0 1px ${PREMIUM_MARK_FILL}`,
      }}
    >
      {showDigit && (
        <span
          aria-hidden="true"
          data-testid="gantt-premium-day-digit"
          className="text-[9px] font-bold leading-none"
          style={{ color: PREMIUM_MARK_KEYLINE }}
        >
          {mark.position}
        </span>
      )}
      {/* Not `aria-label`: `title` on a bare span with no widget role is
          announced inconsistently, so it cannot be the only non-visual
          channel. `sr-only` is announced everywhere. The digit above is
          `aria-hidden` so a reader hears the sentence, not a bare "6". */}
      <span className="sr-only">{title}</span>
    </span>
  );
}

/**
 * Gantt chart for crew members.
 * Bars represent start_date → end_date per member, colored by grade.
 * Supports left-drag, right-drag, and bar-body drag.
 */
/**
 * ONE day toggle. A real `<button role="switch">` — the control has exactly two
 * states and `aria-checked` says which, where `aria-pressed` on a plain button
 * would announce "toggle button" and leave the state to the label.
 *
 * ♿ THREE CHANNELS, because colour is not allowed to be the only one: the FILL
 * (grade colour vs an empty outlined box), the DAY INITIAL printed in it, and
 * the accessible name naming the date AND the state in words. The fill/no-fill
 * distinction is presence-vs-absence rather than a step along an opacity ramp,
 * which matters here — `opacity-*` already carries two meanings in this
 * directory and may not take a third.
 */
/* `ghost`: the CLOSING strip's copy (see `closingDayEditId`) — a picture of the
 * toggles while the row eases shut, never a control: no testid, no tab stop,
 * disabled. */
function DayToggleButton({ member, cell, colors, left, disabled, onToggle, ghost = false }) {
  const label = `${DAY_NAMES[cell.dayIndex]} ${formatDateShort(cell.date)} — ${cell.worked ? 'worked' : 'not worked'}`;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={cell.worked}
      aria-label={label}
      title={label}
      disabled={disabled || ghost}
      tabIndex={ghost ? -1 : undefined}
      data-testid={ghost ? undefined : `crew-gantt-day-toggle-${member.id}-${cell.date}`}
      data-worked={cell.worked ? 'true' : 'false'}
      onClick={() => onToggle(member, cell.date)}
      className={`absolute top-0 flex items-center justify-center text-[10px] font-semibold rounded-sm border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
        cell.worked
          ? `${colors.bg} ${colors.text} border-transparent`
          : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
      }`}
      style={{ left, width: cell.width, height: DAY_TOGGLE_ROW_HEIGHT }}
    >
      <span aria-hidden="true">{DAY_INITIALS[cell.dayIndex]}</span>
    </button>
  );
}

function CrewGanttView({
  members,
  grades,
  canEdit,
  onMemberUpdate,
  onMemberDragPreview,
  totalLabour,
  /* 🔻 WAS `totalComputer`, RENAMED WITH ITS LABEL ON 2026-08-23 — BOTH OR NEITHER.
     The footer below drew this figure under the heading `Computer`, which presented
     computer rental as a peer of allowances rather than one of them; the crew TABLE's
     equivalent column had already been folded into `Allowances` (owner: *"on table view
     please change 'Computer' to 'Allowances'"*), and this footer was recorded at the time
     as CHECKED-NOT-MISSED, with a note saying that bringing it into line needs a SECOND
     prop because "label and figure agree with each other there".
     🔑 THAT IS EXACTLY WHAT THIS IS. Renaming the label alone would have made the footer
     LIE (a computer-only figure under an `Allowances` heading understates by all three
     named slots); renaming the prop alone would have made the LABEL lie. So the prop's
     MEANING changed with its name, and both callers were moved to a figure that really is
     every allowance: `totalAllowancesInclComputer` (= `totalComputer + totalAllowance`) on
     the page-level chart, and the member's published `allowance_total` on the
     single-member one.
     🚨 THE PAGE FOOTER'S `crewGrandTotal` IS UNTOUCHED AND MUST STAY SO — it counts
     computer as its own term because its `totalAllowance` sums only the three named slots,
     so feeding `allowance_total` in there charges every laptop twice (measured £3,755
     against £3,455). This prop is a DISPLAY figure on a different bar; it is not that sum
     and never enters it. */
  totalAllowances,
  /* ─── The day-toggle editor. ALL OPTIONAL — omit every one of them and this
     component renders exactly what it rendered before 2026-08-22, chevron
     included (it is `disabled` with no `onDayEditToggle`). The page owns every
     piece of this state, per this directory's own rule: no fetch, no
     `localStorage`, no derived money state below page level. ─── */
  dayEditMemberId = null,
  dayEditWorkedDates = null,
  dayEditDirty = false,
  dayEditSaving = false,
  /* 🔻 `dayEditCanUndo` WAS HERE AND IS GONE — owner ruling, 2026-08-22:
     *"no need to put 'CTRL +Z to undo' as this is common use"*. Its only
     consumer was the hint span beside the Save button; the SHORTCUT itself is
     untouched and still lives on the page (`handleGanttDayUndo`), where the
     state it unwinds lives. Do not reinstate the prop to "restore the
     affordance" — the absence of the hint is the ruling, not an oversight. */
  onDayEditToggle = null,
  onDayToggle = null,
  onDayEditSave = null,
  onDayEditCancel = null,
  /* ─── LEVEL 2 — THE SECOND EXPAND (PROTOTYPE, 2026-08-22). All optional;
     omit them and this component renders exactly what it rendered before,
     with no Details button and no panel. See DETAILS_PANEL_PLACEMENT. ─── */
  detailsMemberId = null,
  /** 🔑 THE ROW THE PANEL HANGS OFF, which is NOT `detailsMemberId`: that one is
   *  the CONTROL's state and clears the instant the user collapses, while the
   *  panel is still fading out and still needs a row to be positioned against.
   *  The page passes the visible id or, during the fade, the closing one. */
  detailsAnchorId = null,
  detailsPanel = null,
  detailsOpen = false,
  onDetailsToggle = null,
  /**
   * 🔑 THE ONE THING TWO MOUNTED INSTANCES MAY NOT SHARE — added 2026-08-22 for
   * the single-member Gantt inside the crew Full Details panel.
   *
   * The sort lives in `localStorage` under `sfx_crew_gantt_sort_by` /
   * `sfx_crew_gantt_sort_dir`, which are GLOBAL keys: two mounted charts read
   * and write the same two, so sorting inside a panel silently re-orders the
   * page-level chart behind it, and the page-level chart's sort silently
   * re-orders the panel. That is a DEFECT, not a styling preference, which is
   * why it survives the "render the whole chart unchanged" rule the embedded
   * copy is otherwise held to.
   *
   * `false` keeps the sort CONTROL exactly as it is — visible, operable, same
   * markup, same testids — and makes its state purely local to that instance:
   * nothing read at mount, nothing written on click. Sort is meaningless over
   * one member anyway, so the embedded instance loses nothing real.
   *
   * Default `true`, so the page-level chart behaves exactly as before.
   */
  persistSort = true,
  /**
   * 🔲 `flush` (2026-10-05, owner: desktop Labour Flow tightening) — drop the
   * chart's own outer border and rounding, for a caller whose container
   * already frames it (the Labour Flow section card). Without it the page
   * showed a box inside a box inside a card. Default `false`: the single-member
   * chart in the table's details panel keeps its frame. Chrome only — no
   * state, geometry or z-index is touched.
   */
  flush = false,
  /**
   * 📜 `pageScroll` (2026-10-05, owner: *"try removing the scroll in a scroll"*)
   * — ONE PAGE SCROLL for the full Labour Flow chart. Off (default) = the
   * capped body scroller exactly as before, so the single-member chart in the
   * table's panel is unchanged. On:
   *   - the body is NOT a scroller (no `overflow-y`, no cap); the PAGE is the
   *     one vertical scroller `DETAILS_PANEL_PLACEMENT` needs, so the level-2
   *     panel's `top = headerHeight + detailsTop` against the `relative` body
   *     is untouched;
   *   - the date band (still the body's first child, still OUTSIDE the x-only
   *     timeline) pins against the page at `stickyTop` — the caller's MEASURED
   *     `useStickyToolbar().headerTop`, never a constant;
   *   - a vertical drag scrolls the WINDOW (`useDragScroll`'s
   *     `verticalPansWindow`); a horizontal one still pans the timeline;
   *   - the timeline hides its own bar and a shared `ui/StickyScrollbar`,
   *     inset by the label column so its range IS the timeline's, pins to the
   *     window bottom while the timeline overflows (`stickyBottom` = the page's
   *     measured clearance above its fixed totals bar; `null` keeps the
   *     bar's nav-clearing classes). 🪤 No ANCESTOR may be `overflow-hidden`
   *     (it becomes the scroll container both stickies pin to) — clip with
   *     `overflow-clip`.
   */
  pageScroll = false,
  stickyTop = 0,
  stickyBottom = null,
  /**
   * 🧰 `controlsTarget` (2026-10-05, owner: put Sort/Zoom on the Labour Flow
   * toolbar, drop the legend, *"saving a whole row"*). `undefined` (default) =
   * the chart's own controls row with the legend, byte-identical. DEFINED (an
   * element, or `null` while the caller's slot has not mounted) = no controls
   * row at all, and Sort/Zoom are PORTALLED into that element in the toolbar's
   * segmented style. The STATE stays here (zoom via `useGanttZoom`, the sort keys) — the
   * caller only supplies where the controls draw. Without the legend the grade
   * still has three channels (bar colour, the label row's `title`, the
   * tooltip's `Grade:` line) and an Off week gets a tooltip line.
   */
  controlsTarget,
  /**
   * 🔑 HOW THE OPEN STRIP'S FIGURES ARE DOING — `'idle'` | `'pending'` |
   * `'failed'`, 2026-08-23. It governs the member named by `dayEditMemberId`
   * and NOBODY ELSE: every other row is on its saved figure, which is true and
   * settled, and marking those would be noise.
   *
   * The page owns it because the page owns the request (`useCrewCostPreview`
   * against `POST /crew-members/:id/cost-preview`). This component draws the
   * three states and knows nothing about the fetch — the same contract every
   * other prop here has. See `LIVE_COST_PENDING_MARK` for what each one draws
   * and why `failed` must not fall back to the saved number.
   */
  dayEditPreviewState = 'idle',
}) {
  // VAT mode is bound to the active production via DepartmentContext.
  const { formatMoney } = useVatMode();
  /* 🔑 ONE PREDICATE, THREE READERS — the price cell, its `title`, and the
     drag tooltip's Budget line. Written once so the mark and the sentence
     cannot disagree about which state a row is in. */
  const liveCostStateFor = (memberId) => (
    dayEditMemberId != null && memberId === dayEditMemberId ? dayEditPreviewState : 'idle'
  );
  const liveCostTitleFor = (memberId) => {
    const state = liveCostStateFor(memberId);
    if (state === 'pending') return LIVE_COST_PENDING_TITLE;
    if (state === 'failed') return LIVE_COST_FAILED_TITLE;
    return undefined;   // never `''` — an empty `title` renders a blank popup
  };
  /* 🪤 The key literals stay INLINE at every read and write. Hoisting them into
     a constant would hide them from `scripts/check-bloat.js`, which greps the
     source text for `sfx_` keys and can see a key written through a constant in
     NEITHER direction (root `CLAUDE.md`, `sfx_breakdown_col_widths`). */
  const [sortBy, setSortBy] = useState(
    () => (persistSort ? localStorage.getItem('sfx_crew_gantt_sort_by') : null) || 'name'
  );
  const [sortDir, setSortDir] = useState(
    () => (persistSort ? localStorage.getItem('sfx_crew_gantt_sort_dir') : null) || 'asc'
  );
  /* The two sort writes, shared by the chart's own controls row and the
     toolbar copy `controlsTarget` portals. Key literals stay inline (check-bloat). */
  const pickSort = (s) => {
    setSortBy(s);
    if (persistSort) localStorage.setItem('sfx_crew_gantt_sort_by', s);
  };
  const flipSortDir = () => {
    if (sortBy === 'date') {
      const next = sortDir === 'end' ? 'start' : 'end';
      setSortDir(next);
      if (persistSort) localStorage.setItem('sfx_crew_gantt_sort_dir', next);
    } else {
      const next = sortDir === 'asc' ? 'desc' : 'asc';
      setSortDir(next);
      if (persistSort) localStorage.setItem('sfx_crew_gantt_sort_dir', next);
    }
  };
  /* Toolbar copy only: switching field starts it at its own first state
     (Name A→Z, Date by start), so the next click on it always visibly flips —
     a leftover 'end'/'desc' from the other field would make it a no-op. */
  const pickSortFresh = (s) => {
    pickSort(s);
    const first = s === 'date' ? 'start' : 'asc';
    setSortDir(first);
    if (persistSort) localStorage.setItem('sfx_crew_gantt_sort_dir', first);
  };
  const sortDirTitle = sortBy === 'date' ? (sortDir === 'end' ? 'Sort by end date' : 'Sort by start date') : (sortDir === 'asc' ? 'A → Z' : 'Z → A');
  const timelineRef = useRef(null);
  // Zoom state, pixels per day and the keep-the-centre rule (shared).
  const { zoom, dayWidth, zoomTo, applyCentre } = useGanttZoom(timelineRef);
  const todayLineRef = useRef(null);
  // The "scroll to today" effect's own success latch — see that effect,
  // declared further down beside `dateHeaders`, for why this is a pair of
  // refs and not a dependency-array trick.
  const hasPositionedTodayRef = useRef(false);
  const positionedZoomRef = useRef(null);

  /**
   * 🔻 THE VERTICAL SCROLLER, HOISTED ABOVE BOTH COLUMNS ON 2026-08-23.
   * See `DETAILS_PANEL_PLACEMENT`: the timeline owns the HORIZONTAL axis and
   * this box owns the VERTICAL one, which is what lets the level-2 panel span
   * the member column as well as the timeline.
   *
   * 🗑️ WHAT THIS REPLACED, so nobody rebuilds it: the two boxes each owned their
   * own vertical scroll and were held in step by hand —
   * `labels.scrollTop = timeline.scrollTop`, written in an `onScroll` here AND
   * again inside `useDragScroll`'s pan handler, both reaching the label column
   * through `document.getElementById`. That needed a per-instance DOM id
   * (`crew-gantt-labels-${useId()}`), because `getElementById` answers the FIRST
   * match in document order and TWO charts are mounted the moment the crew Full
   * Details panel is open in Gantt view. One scroller means none of that exists.
   * The stable `data-crew-gantt-labels` attribute stays — it is what the tests
   * select on, and it never depended on the sync.
   */
  const bodyScrollRef = useRef(null);

  /* Click-and-drag panning. TWO CALLS, ONE PER AXIS, because the two axes now
     live on two different elements — and BOTH are required behaviour, not a
     nicety: owner, 2026-08-22, *"the user wants to click and drag to scroll the
     chart"*, and again *"clicking on the bar should do nothing apart from let us
     scroll the entire chart"*.
     🪤 `mousedown` BUBBLES, so a drag started on the timeline reaches this
     element's listener too and both axes pan from one gesture, exactly as the
     single `axis: 'both'` call used to. Each call skips children marked
     `data-pan-skip` (and real controls), so the bar/handle drag handlers and the
     details panel still own their own gestures. */
  useDragScroll(timelineRef, { axis: 'x' });
  // `pageScroll`: the body is no longer a scroller, so its vertical pan moves the WINDOW.
  useDragScroll(bodyScrollRef, { axis: 'y', verticalPansWindow: pageScroll });
  /* 📜 The timeline's horizontal frame — `hooks/useGanttScrollFrame`, shared
     with the hires, cross-team and Labour Gantts (extracted from here
     2026-10-05, moved not rewritten):
       - `pageScroll`'s sticky horizontal bar, linked two-way with the
         timeline, rendered only while the timeline overflows;
       - 🌫️ `pageScroll`'s edge fade (owner, 2026-10-05: *"fade the edge of the
         gannt chart to signify scrolling"*) — THE `ui/edgeFade` mask on the
         timeline AND the date band's clip, never on the label column; a mask,
         never an overlay and never `opacity-*` (spoken for twice here);
       - 📌 THE STICKY DATE BAND's wiring (see the scroll-model docblock above
         `DETAILS_PANEL_PLACEMENT`'s rules): the band lives OUTSIDE the
         timeline, so its clip FOLLOWS the timeline one way, a drag on it pans
         the TIMELINE (the press also bubbles to the body scroller, so it pans
         vertically too), and a `deltaX` wheel on it is forwarded.
     🚫 Refs only — `getElementById` is banned in this file (two charts can be
     mounted at once; `gantt-label-scroll-sync.test.js` pins its absence). */
  const {
    stickyBarRef,
    overflows: timelineOverflows,
    scrollWidth: timelineScrollWidth,
    fadeStyle: timelineFadeStyle,
    readFade: readTimelineFade,
    dateBandRef,
    dateAxisClipRef,
    handleBandWheel,
  } = useGanttScrollFrame(timelineRef, { fade: pageScroll });
  // The bottom of the pinned date band (measured live): a Gantt card that
  // would sit above it flips BELOW its bar (`gantt/GanttBarTooltip`).
  const getPinnedBottom = useCallback(() => (dateBandRef.current
    ? dateBandRef.current.getBoundingClientRect().bottom : 0), [dateBandRef]);

  /* The level-2 panel's own height, so `transition-[height]` has something to
     animate TO — CSS cannot ease to `auto`. This is presentation, not state:
     the directory's rule is no fetch, no `localStorage` and no derived MONEY
     state below page level, and a measured pixel height is none of those.
     🪤 jsdom reports the height as 0 — it MEASURES SUCCESSFULLY, at zero (see
     the hook's own docblock), so nothing in a test can distinguish a measured
     height from a literal 0. That is why the panel's assertions are structural.
     🔑 The panel's WIDTH has no such hook any more — `useMeasuredWidth` was
     deleted here with the sticky placement (see `DETAILS_PANEL_PLACEMENT`);
     `left-0 right-0` of a box that does not scroll sideways needs no
     measurement. Height still does, because there is nothing to measure it
     against. */
  const [detailsBodyRef, detailsBodyHeight] = useMeasuredHeight();

  // Drag state: { member, handle: 'left'|'right'|'body', startX, originalStart, originalEnd, currentStart, currentEnd }
  const [dragState, setDragState] = useState(null);
  // The bar's hover card — `gantt/GanttBarTooltip` (shared with the hires and
  // cross-team Gantts); a drag suppresses it.
  const {
    tooltip, setTooltip, show: showBarTooltip, hide: hideTooltip, cancelHide: cancelHideTooltip,
  } = useGanttBarTooltip(!!dragState);
  // The drag card's anchor (the dragged member's bar) and the pinned chrome's
  // bottom it must not sit under — both read live, every frame, by the card.
  const dragMemberId = dragState ? dragState.member.id : null;
  /* The drag card's anchor: the dragged bar PLUS its day strip below it, as one
     box, so a card placed `prefer="below"` sits UNDER the day toggles (owner
     2026-10-06: *"the tool tip should be below the days"*) and, when it must
     flip, ABOVE the bar — never over either. Only a rect is needed. */
  const getDragBarEl = useCallback(() => {
    if (dragMemberId == null || !timelineRef.current) return null;
    const bar = timelineRef.current.querySelector(`[data-testid="crew-gantt-bar-${dragMemberId}"]`);
    if (!bar) return null;
    const strip = timelineRef.current.querySelector(`[data-testid="crew-gantt-day-strip-${dragMemberId}"]`);
    if (!strip) return bar;
    return {
      getBoundingClientRect: () => {
        const a = bar.getBoundingClientRect();
        const b = strip.getBoundingClientRect();
        const top = Math.min(a.top, b.top);
        const bottom = Math.max(a.top + a.height, b.top + b.height);
        return { left: a.left, top, width: a.width, height: bottom - top };
      },
    };
  }, [dragMemberId]);

  /* Placeholder drag state — DELIBERATELY SEPARATE from `dragState` above.
   * `dragState`/`handleDragMove`/`handleDragEnd` assume `member.start_date`
   * and `member.end_date` are real strings (`.slice(0, 10)` at drag start,
   * left/right-handle arithmetic against them) — a dateless member has
   * neither. Feeding it a synthetic pair would also feed
   * `generateWeekSegments`/`getBarStyle`/the tooltip a fake engagement for a
   * member who has none, which is exactly the kind of invented date this
   * feature must not let leak into anything but the commit itself.
   * `{ memberId, startX, deltaDays }` — whole-block move only, no left/right
   * resize (there is nothing to resize yet). */
  const [placeholderDrag, setPlaceholderDrag] = useState(null);


  // Build grade → color map
  const gradeColorMap = useMemo(() => {
    const map = {};
    const uniqueGrades = grades.length > 0
      ? grades
      : [...new Set(members.map(m => m.grade_name))].filter(Boolean).map((name, i) => ({ id: i, name }));
    uniqueGrades.forEach((g, i) => {
      map[g.id || g.name] = GRADE_COLORS[i % GRADE_COLORS.length];
    });
    return map;
  }, [grades, members]);

  function getGradeColor(member) {
    return gradeColorMap[member.crew_grade_id] || gradeColorMap[member.grade_name] || GRADE_COLORS[0];
  }

  // Filter members that have both dates, then sort
  const sortedMembers = useMemo(() => {
    const filtered = members.filter(m => m.start_date && m.end_date);
    return filtered.sort((a, b) => {
      if (sortBy === 'date') {
        const field = sortDir === 'end' ? 'end_date' : 'start_date';
        return (a[field] || '').localeCompare(b[field] || '');
      }
      const cmp = (a.name || '').localeCompare(b.name || '');
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }, [members, sortBy, sortDir]);

  /* Owner ruling 2026-10-11: a row with unsaved changes STAYS WHERE IT IS —
     dragging a bar under a date sort used to send the row to its new sorted
     place at once, often off the screen. It moves on Save, and the chart then
     scrolls to it and marks it briefly; a sort click still re-sorts at once.
     ORDER only: the row's dates are the draft, as before. */
  const { rows: validMembers, revealId: movedRowId } = useHeldRowOrder(sortedMembers, {
    hold: dayEditDirty || dayEditSaving,
    sortKey: `${sortBy}:${sortDir}`,
    editedId: dayEditMemberId,
  });
  const labelsRef = useRef(null);
  useEffect(() => {
    if (movedRowId == null || !labelsRef.current) return;
    const el = labelsRef.current.querySelector(`[data-testid="crew-gantt-member-row-${movedRowId}"]`);
    if (!el || typeof el.scrollIntoView !== 'function') return;
    const calm = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: calm ? 'auto' : 'smooth' });
  }, [movedRowId]);

  const membersWithoutDates = useMemo(() =>
    members.filter(m => !m.start_date || !m.end_date),
  [members]);

  // Compute timeline range
  const { timelineStart, timelineEnd, totalDays } = useMemo(() => {
    if (validMembers.length === 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return { timelineStart: today, timelineEnd: addDays(today, 30), totalDays: 30 };
    }
    let minDate = null;
    let maxDate = null;
    validMembers.forEach(m => {
      const from = parseDate(m.start_date);
      const to = parseDate(m.end_date);
      if (from && (!minDate || from < minDate)) minDate = from;
      if (to && (!maxDate || to > maxDate)) maxDate = to;
    });
    const start = addDays(minDate || new Date(), -7);
    // Snap to Monday
    const dow = start.getDay();
    if (dow !== 1) {
      start.setDate(start.getDate() - ((dow + 6) % 7));
    }
    const end = addDays(maxDate || new Date(), 14);
    const total = daysDiff(start, end);
    return { timelineStart: start, timelineEnd: end, totalDays: Math.max(total, 30) };
  }, [validMembers]);

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  // The dateless placeholder's week — the FIRST VISIBLE Monday, never "the
  // current week". See the block above `firstVisibleMonday` for why forward
  // snapping (not a backward floor) is required here.
  const placeholderWeekStart = useMemo(
    () => firstVisibleMonday(timelineStart),
    [timelineStart]
  );

  /*
   * The 6th/7th-day premium marks, MEMOISED PER MEMBER — deliberately, and
   * not for tidiness. `generateWeekSegments` is recomputed on every render
   * for every member because a drag genuinely changes its input. These marks
   * do NOT: a date drag moves the bar and leaves the member's day rows alone,
   * which is why they are suppressed mid-drag in the first place. Without
   * this, every drag frame would re-run `runPositions` over up to 730 day
   * rows for each of ~150 members to produce an answer that cannot have
   * changed.
   * 🔑 `timelineStart` is itself memoised on `validMembers`, so this
   * recomputes only when the crew data or the zoom changes.
   */
  const premiumMarksByMember = useMemo(() => {
    const map = new Map();
    for (const m of validMembers) {
      const marks = computePremiumDayMarks(
        m, timelineStart, dayWidth,
        m.id === dayEditMemberId ? dayEditWorkedDates : null
      );
      if (marks.length > 0) map.set(m.id, marks);
    }
    return map;
    // ⚠️ `dayEditMemberId` / `dayEditWorkedDates` ARE REAL DEPENDENCIES since
    // 2026-08-22 — the marks now move when a toggle changes the run, and
    // omitting them here would freeze the marks on the set that happened to be
    // resolved when the strip opened. That is the memo silently re-creating
    // exactly the disagreement the shared set exists to prevent.
  }, [validMembers, timelineStart, dayWidth, dayEditMemberId, dayEditWorkedDates]);

  const todayOffset = daysDiff(timelineStart, today);

  const totalWidth = totalDays * dayWidth;
  const rowHeight = 42;
  /* 📏 `pageScroll` (the Labour Flow chart) takes a 32px header (owner,
     2026-10-05: *"can we also reduce the height on the gantt chart's 'Member |
     2 Mar | 9 Mar | 16 Mar …' header?"*) — one text-xs row (+ the 10px week-
     zoom sublabel) fits. PASSED to the shared axis, never a change to
     `GANTT_HEADER_HEIGHT` (the Breakdown timeline reads it). Every reader below
     takes `headerHeight`, so the band, the axis and the level-2 panel's `top`
     move together. The single-member chart keeps 48. */
  const headerHeight = pageScroll ? CREW_PAGE_SCROLL_HEADER_HEIGHT : GANTT_HEADER_HEIGHT;
  /**
   * 📏 220 → 280, owner-authorised (*"we can make this column a little wide to
   * include the price"*). The arithmetic, so the number is a decision and not a
   * guess — every term is a real class on the row, read off the markup below:
   *
   *   px-3 padding                     12 + 12 = 24
   *   chevron  (-ml-0.5 + p-1 + w-3.5 + mr-1)  = 24
   *   grade dot (w-2 + mr-1.5)                 = 14
   *   user-status dot (w-1.5 + mr-1.5)         = 12   ← present on linked members
   *   name, 15 chars at text-sm                ≈ 112
   *   gap-2 before the price                   =  8
   *   price, 11 chars at text-xs tabular-nums  ≈  72
   *                                            ───────
   *                                              266  → 280 with slack
   *
   * 🔴 MEASURED AGAINST THE REAL DATA, read-only from `server/data/ordering.db`
   * on 2026-08-22: 150 crew rows, name length p50 **13**, p90 **15**, max **20**
   * (`Charlotte Fitzgerald`); highest grade rate £3,200/wk and longest
   * engagement 139 days ≈ 20 weeks, so the largest budget figure the data can
   * produce is about **£64,000.00 — 10 characters**. The 11-character budget
   * above (`£123,456.00`) is deliberate headroom. So the owner's 15-character
   * rule truncates roughly the top decile of names and nothing else, which is
   * what *"truncate slightly if required"* asks for.
   *
   * 🚨 THE PRICE NEVER TRUNCATES — a clipped money figure is a WRONG money
   * figure (`£12,45…` reads as a different number). It is `flex-shrink-0`; the
   * NAME carries `min-w-0 truncate` and absorbs any shortfall. If the two ever
   * compete, the name loses, by construction rather than by tuning.
   *
   * ⚠️ THE PIXEL FIGURES ABOVE ARE ARITHMETIC OVER THE CSS BOX MODEL PLUS
   * PER-GLYPH ESTIMATES FOR Inter — NOT a Chromium measurement, which jsdom
   * cannot provide. Say so rather than implying otherwise. The DESIGN is what
   * makes it safe regardless: the price is unshrinkable and the name is a
   * character rule, so an inaccurate estimate costs a few characters of name,
   * never a wrong number. The look is a BROWSER check and the owner will
   * eyeball it.
   *
   * 🪤 One constant, two consumers, and they are in the SAME element: the
   * `Member` header cell and the rows both sit inside the one
   * `style={{ width: labelWidth }}` container, so the heading cannot part
   * company with its column. (`components/hires/GanttView.js` has its own,
   * unrelated `labelWidth = 220`; it is a different Gantt and does not move.)
   */
  const labelWidth = 280;

  /* ─── DAY-TOGGLE EDITOR — derived state ────────────────────────────────── */

  // Week + month zoom (see `DAY_EDIT_ZOOMS`). The chevron is rendered at every
  // zoom and DISABLED here, never hidden.
  const dayEditingAllowed = DAY_EDIT_ZOOMS.includes(zoom);
  const dayEditAvailable = canEdit && typeof onDayEditToggle === 'function';
  const dayEditDisabledReason = !dayEditAvailable
    ? DAY_EDIT_NO_PERMISSION_TITLE
    : (!dayEditingAllowed ? DAY_EDIT_DISABLED_TITLE : null);

  /* ═══════════ EXPAND-TO-EDIT — owner ruling, 2026-08-22 ═══════════════════
   *
   * Verbatim: *"we should not allow editing and dragging of the gantt chart
   * unless the gantt chart has been expanded by clicking on the chevron to
   * expand it. Quarter view should not be draggable or expandable."*
   *
   * 🔴 WHY THIS IS SAFE-MAKING RATHER THAN TIDYING, AND THE MEASUREMENT IS THE
   * ARGUMENT. `handleDragMove` commits the moment the pointer movement rounds
   * to ONE DAY (`pxToDays` is `Math.round(px / dayWidth)`) — **there is no
   * movement threshold anywhere in this component.** Before this change the
   * only gate on a drag was `canEdit`, so at `month` zoom a **16px** pointer
   * slip and at `quarter` zoom a **5px** slip already moved a crew member's
   * employment dates, and therefore their pay. Expansion is now a second,
   * deliberate act that has to precede any of that.
   *
   * 🔑 ONE ZOOM LIST, NOT TWO — the ruling on the brief's open question.
   * `DAY_EDIT_ZOOMS` already excludes `quarter`, and the drag gate REUSES it
   * through `dayEditingAllowed` rather than declaring a second list. It is not
   * merely convenient: the owner's sentence makes "draggable" and "expandable"
   * the SAME set (`quarter` is neither), so two constants could only ever
   * disagree, and a silently-diverging second list of "editable zooms" is the
   * duplication this repo's Code Unification Principle exists to stop.
   *
   * 🪤 THE `dayEditingAllowed` TERM IS NOT REDUNDANT WITH `dayEditMemberId`.
   * A strip opened at week zoom deliberately STAYS OPEN when the user zooms
   * out to quarter (collapsing it would look like the unsaved edit being
   * thrown away — see the strip's own notice branch). In that state the row IS
   * expanded while quarter zoom is showing, and the owner's ruling says quarter
   * is not draggable, full stop. Dropping this term would leave exactly one
   * reachable 5px-per-day drag.
   *
   * ⚠️ A DECLARED CONSEQUENCE, NOT A BUG: a caller that does not pass
   * `onDayEditToggle` can never expand a row, so it can never drag either.
   * That follows from the ruling rather than working around it — a surface
   * with no way to expand has no way to reach the drag. `pages/BudgetCostCodes.js`
   * is the only production caller and it wires all of it.
   */
  const canExpandRows = dayEditAvailable && dayEditingAllowed;

  /* The inactive-row treatment, one resolution point for the timeline row.
   * `someRowExpanded` is the gate the owner's ask implies: with NOTHING
   * expanded there is no row to make prominent, so nothing may be treated.
   * `isRowGreyed` answers per member and is the ONLY place the treatment is
   * decided. The winner does not tint — see the docblock above. */
  const someRowExpanded = dayEditMemberId != null;
  const isRowGreyed = useCallback(
    (memberId) => someRowExpanded && memberId !== dayEditMemberId,
    [someRowExpanded, dayEditMemberId]
  );
  const canDragMember = useCallback(
    (member) => canEdit && dayEditingAllowed && !!member && member.id === dayEditMemberId,
    [canEdit, dayEditingAllowed, dayEditMemberId]
  );

  /**
   * The horizontal slice of the timeline that is actually on screen, as a day
   * index range, or `null` when nothing has been measured.
   *
   * 🔑 A two-year engagement is 728 days. Rendering 728 focusable controls in
   * one row is 728 tab stops and 728 event handlers for a strip the user can
   * see forty of, so the toggles are bounded to the scrollport (plus one day of
   * slack each side, so a partially-visible day is still clickable).
   * 🪤 jsdom answers 0 for every measurement, so `width === 0` MUST mean
   * "unmeasured, render the whole bar" and not "nothing is visible" — the
   * second reading makes this feature untestable and, worse, makes it render
   * nothing for one frame before the browser's first measurement lands.
   */
  const [scrollView, setScrollView] = useState({ left: 0, width: 0 });
  const measureScrollView = useCallback(() => {
    const el = timelineRef.current;
    if (!el) return;
    setScrollView((prev) => (
      prev.left === el.scrollLeft && prev.width === el.clientWidth
        ? prev
        : { left: el.scrollLeft, width: el.clientWidth }
    ));
  }, []);
  useEffect(() => {
    measureScrollView();
    window.addEventListener('resize', measureScrollView);
    return () => window.removeEventListener('resize', measureScrollView);
  }, [measureScrollView, dayWidth, validMembers.length, dayEditMemberId]);

  const visibleDayRange = useMemo(() => {
    if (!scrollView.width) return null; // unmeasured — see the block above
    return {
      first: Math.floor(scrollView.left / dayWidth) - 1,
      last: Math.ceil((scrollView.left + scrollView.width) / dayWidth) + 1,
    };
  }, [scrollView, dayWidth]);

  /* 🔑 RULE 3 of `DETAILS_PANEL_PLACEMENT`. The panel is only ever as tall as it
     has been MEASURED to be, and only while it is open — so a closed panel, an
     unmeasured first paint and a missing anchor all contribute exactly 0 and the
     chart is byte-identical to what it was before this feature. */
  const detailsAnchorVisible = detailsPanel != null && detailsAnchorId != null
    && validMembers.some((m) => m.id === detailsAnchorId);
  const detailsHeight = detailsAnchorVisible && detailsOpen ? (detailsBodyHeight || 0) : 0;

  // A row grows by one toggle strip while its member is being edited, and by the
  // details panel while that member's level 2 is open.
  // 🚨 EVERY HEIGHT IN THE CHART IS DERIVED FROM THIS — the label column row, the
  // timeline row, `chartHeight`, the background grid, the today marker AND the
  // panel's own `top`. They were `validMembers.length * rowHeight` once and
  // stopped short of the last row the moment any row grew; a second arithmetic
  // for the panel's offset would reopen exactly that, one axis over.
  /* 🔽 THE CLOSE IS ANIMATED TOO (owner, 2026-10-06: *"add the smooth
   * animation to the closing gantt bar"*). The row's height already eases
   * shut (`ROW_EXPAND_TRANSITION`), but the strip's CONTENT unmounted the
   * instant `dayEditMemberId` cleared, so the row shrank empty and read as a
   * pop. The closing member keeps its timeline strip mounted, as inert GHOST
   * toggles (no testid, no tab stop), for the transition's length; `rowHeightFor` deliberately ignores
   * it, so the height still goes to its closed value and the overflow-hidden
   * row clips the strip as it shrinks. Reduced motion: the row is already
   * closed, so the lingering strip is clipped to nothing. */
  const [closingDayEditId, setClosingDayEditId] = useState(null);
  const prevDayEditIdRef = useRef(dayEditMemberId);
  useEffect(() => {
    const prev = prevDayEditIdRef.current;
    prevDayEditIdRef.current = dayEditMemberId;
    if (prev == null || prev === dayEditMemberId) return undefined;
    setClosingDayEditId(prev);
    const t = setTimeout(() => setClosingDayEditId(null), 220);
    return () => clearTimeout(t);
  }, [dayEditMemberId]);
  const rowHeightFor = useCallback(
    (member) => rowHeight
      + (member.id === dayEditMemberId ? DAY_TOGGLE_ROW_HEIGHT + DAY_TOGGLE_ROW_GAP : 0)
      + (member.id === detailsAnchorId ? detailsHeight : 0),
    [dayEditMemberId, detailsAnchorId, detailsHeight]
  );
  // 🔻 Placeholder rows are suppressed entirely at `quarter` zoom (see the
  // block above the placeholder bar's JSX for why), so their height only
  // joins the grid/today-marker span while they are actually drawn.
  const placeholderRowsVisible = zoom !== 'quarter' && membersWithoutDates.length > 0;
  const chartHeight = useMemo(
    () => validMembers.reduce((sum, m) => sum + rowHeightFor(m), 0)
      + (placeholderRowsVisible ? membersWithoutDates.length * rowHeight : 0),
    [validMembers, rowHeightFor, placeholderRowsVisible, membersWithoutDates.length]
  );

  /** The panel's `top` inside the rows container: every row above it, plus its
   *  own row and (if open) its own day strip. `null` when the anchor is not on
   *  the chart — a member can lose their dates and be filtered out mid-fade. */
  const detailsTop = useMemo(() => {
    if (!detailsAnchorVisible) return null;
    let y = 0;
    for (const m of validMembers) {
      if (m.id === detailsAnchorId) {
        return y + rowHeight + (m.id === dayEditMemberId ? DAY_TOGGLE_ROW_HEIGHT + DAY_TOGGLE_ROW_GAP : 0);
      }
      y += rowHeightFor(m);
    }
    return null;
  }, [detailsAnchorVisible, validMembers, detailsAnchorId, dayEditMemberId, rowHeightFor]);

  /**
   * Scroll to today ONCE the chart is measurable, and again on a genuine
   * zoom change — never merely because an edit produced a new `validMembers`
   * array. 🚨 **THIS USED TO DEPEND ON `[todayOffset, dayWidth, validMembers]`
   * AND SNAPPED THE VIEWPORT BACK TO TODAY AFTER EVERY EDIT** (owner report,
   * 2026-08-29, of adjusting a bar in the past). `validMembers` is a brand
   * new array on every render regardless of WHAT changed — sort order, a
   * dragged date, a settled cost preview — because its own `useMemo` filters
   * and sorts `members` fresh each time (see its definition above); listing
   * it here re-ran this effect on every one of those. `todayOffset` doubled
   * the bug: it is `daysDiff(timelineStart, today)`, and `timelineStart` is
   * ITSELF derived from `validMembers` (the earliest member's `start_date`
   * minus 7 days, snapped to Monday), so an edit that moved a member's
   * `start_date` earlier moved `timelineStart`, which moved `todayOffset`,
   * which would have re-triggered this effect a SECOND way even with
   * `validMembers` alone fixed.
   *
   * 🪤 A NARROWER DEPENDENCY ARRAY (`[zoom, hasMembers]`, an earlier version
   * of this fix) IS NOT ENOUGH ON ITS OWN AND WAS CAUGHT BEFORE IT SHIPPED.
   * The old broad array's real job was never "list every legitimate
   * trigger" — it was that a wide, twitchy dependency list gave this effect
   * MANY chances to find `todayLineRef`/`timelineRef` populated, because
   * `todayLineRef` only mounts when `todayOffset` falls inside
   * `[0, totalDays]`, and on a slow/staged data load that can still be false
   * on the exact render where a narrower trigger last fires. A dependency
   * array can only ever record "what changed", never "did the last attempt
   * actually succeed" — so narrowing it can silently delete the one retry
   * that would have found the refs ready, which is precisely the
   * initial-scroll-never-happens failure this rule exists to avoid.
   *
   * So this effect has **NO DEPENDENCY ARRAY AT ALL** — React runs it after
   * EVERY commit — and does its own bookkeeping with two refs instead:
   *  - `hasPositionedTodayRef` — have we EVER successfully written
   *    `scrollLeft`. Stays `false` (and this effect keeps retrying, for
   *    free, on every subsequent render) until a render finds both refs
   *    populated, however many renders that takes.
   *  - `positionedZoomRef` — which `zoom` we last positioned FOR. A zoom
   *    change reads as `positionedZoomRef.current !== zoom` and is treated
   *    like "not yet positioned", which is the one legitimate re-trigger
   *    after the first success.
   * Once positioned for the current zoom, EVERY subsequent call — and an
   * edit causes one on every render, same as before — hits the early
   * `return` before touching `scrollLeft`. `dayWidth` is a pure function of
   * `zoom` (`hooks/useGanttZoom`) and is read below for the arithmetic;
   * it needs no bookkeeping of its own.
   *
   * A component never gets a stale set of these refs: they are declared
   * with the component's other `useRef`s and a fresh instance (a real
   * remount, not a re-render) starts both at their initial values again.
   *
   * ⚠️ A CHART WHOSE MEMBERS NEVER SPAN TODAY (e.g. a crew wholly in the
   * past) never mounts `todayLineRef` and so never gets an initial scroll —
   * true of this effect before this fix and after it alike; not something
   * either version of this dependency list controls, and not this bug.
   *
   * Pinned by `CrewGanttView.scrollSnap.test.js`, including a staged-arrival
   * case (empty → populated over two renders, mirroring an async fetch) that
   * a `[zoom, hasMembers]`-only version cannot be told apart from by a
   * single-render test; see that file's header for what jsdom cannot prove.
   */
  useEffect(() => {
    if (!timelineRef.current) return;
    const zoomChanged = positionedZoomRef.current !== null && positionedZoomRef.current !== zoom;
    // A zoom change keeps the date in the middle of the view (owner 2026-10-13);
    // only the first positioning goes to today. It runs BEFORE the today-line
    // guard: a chart that never spans today has no line to mount, and until
    // 2026-10-13 (review F4) that guard also swallowed its zoom changes.
    if (zoomChanged && applyCentre()) { positionedZoomRef.current = zoom; return; }
    if (!todayLineRef.current) { positionedZoomRef.current = zoom; return; }
    if (hasPositionedTodayRef.current && !zoomChanged) return;

    const container = timelineRef.current;
    const todayPos = todayOffset * dayWidth;
    const scrollTo = todayPos - container.clientWidth / 3;
    container.scrollLeft = Math.max(0, scrollTo);

    hasPositionedTodayRef.current = true;
    positionedZoomRef.current = zoom;
  });

  // Build date headers
  // THE shared date-axis cells (`utils/ganttDates`); this chart built its own
  // identical copy until 2026-10-13.
  const dateHeaders = useMemo(() => buildDateHeaders(zoom, timelineStart, totalDays), [zoom, timelineStart, totalDays]);

  function getBarStyle(member) {
    const from = parseDate(member.start_date);
    const to = parseDate(member.end_date);
    if (!from || !to) return null;
    const startDay = daysDiff(timelineStart, from);
    // +1: the day ON end_date is worked and drawn (owner ruling, 2026-08-24 —
    // `end_date` is the LAST WORKING DAY, inclusive). A same-day engagement is
    // one day wide. ⚠️ `getDragBar` below is a copy of this geometry for the
    // in-flight drag frame — the two must move together or the bar changes
    // width the instant a drag starts.
    const duration = Math.max(daysDiff(from, to) + 1, 1);
    return {
      left: startDay * dayWidth,
      width: Math.max(duration * dayWidth, 4),
    };
  }

  // Tooltip
  function showTooltip(e, member) {
    if (dragState) return;
    showBarTooltip(e, {
      member,
      // The legend's Off swatch is gone on the toolbar layout; the tooltip names it.
      offWeeks: generateWeekSegments(member, timelineStart, dayWidth, null)
        .filter((seg) => segmentShade(seg) === 0).length,
    });
  }

  // --- DRAG LOGIC ---
  const pxToDays = useCallback((px) => Math.round(px / dayWidth), [dayWidth]);

  /* The click-vs-drag latch (see `CLICK_DRAG_THRESHOLD_PX`), and the one-shot
     suppressor for the `click` a browser fires after the mouseup: once the
     strip collapses, that click would land on a now-COLLAPSED bar and expand
     it straight back. */
  const pointerGesture = useRef(null);
  const suppressBarClickRef = useRef(false);
  const handleDragStart = useCallback((e, member, handle) => {
    // 🔒 THE GATE LIVES HERE, NOT ONLY IN THE JSX, and that is deliberate: this
    // is the ONE choke point all three handles (left, right, body) pass
    // through, so a future handle added without re-reading the render tree
    // still cannot start a drag on a collapsed row. See `canDragMember`.
    if (!canDragMember(member)) return;
    e.preventDefault();
    e.stopPropagation();

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    pointerGesture.current = {
      startX: clientX,
      startY: clientY,
      threshold: e.touches ? TOUCH_CLICK_DRAG_THRESHOLD_PX : CLICK_DRAG_THRESHOLD_PX,
      exceededThreshold: false,
    };
    setDragState({
      member,
      handle, // 'left', 'right', 'body'
      startX: clientX,
      originalStart: member.start_date.slice(0, 10),
      originalEnd: member.end_date.slice(0, 10),
      currentStart: member.start_date.slice(0, 10),
      currentEnd: member.end_date.slice(0, 10),
    });
    setTooltip(null);
  }, [canDragMember, setTooltip]);

  const handleDragMove = useCallback((e) => {
    if (!dragState) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    // 🔑 THE LATCH — first, before any early return: once past the radius the
    // gesture is a drag for good, even if it comes back to its origin.
    const g = pointerGesture.current;
    if (g && !g.exceededThreshold && Math.hypot(clientX - g.startX, clientY - g.startY) > g.threshold) {
      g.exceededThreshold = true;
    }

    const deltaX = clientX - dragState.startX;
    const deltaDays = pxToDays(deltaX);
    if (deltaDays === 0 && dragState.currentStart === dragState.originalStart && dragState.currentEnd === dragState.originalEnd) return;

    const origStart = parseDate(dragState.originalStart);
    const origEnd = parseDate(dragState.originalEnd);

    let newStartStr, newEndStr;
    if (dragState.handle === 'right') {
      const newEnd = addDays(origEnd, deltaDays);
      // `<`, not `<=`: start === end is a legal ONE-DAY engagement now that
      // `end_date` is the last worked day (owner ruling, 2026-08-24).
      if (newEnd < origStart) return;
      newStartStr = dragState.originalStart;
      newEndStr = toDateStr(newEnd);
    } else if (dragState.handle === 'left') {
      const newStart = addDays(origStart, deltaDays);
      if (newStart > origEnd) return;
      newStartStr = toDateStr(newStart);
      newEndStr = dragState.originalEnd;
    } else {
      const newStart = addDays(origStart, deltaDays);
      const newEnd = addDays(origEnd, deltaDays);
      newStartStr = toDateStr(newStart);
      newEndStr = toDateStr(newEnd);
    }

    setDragState(prev => ({ ...prev, currentStart: newStartStr, currentEnd: newEndStr }));

    /* Notify the page of the DATES. 🔻 IT CARRIED `budget_cost` AND
       `total_weeks` UNTIL 2026-08-23 AND NO LONGER DOES — owner: *"when
       dragging the totals update but seems to not calculate the off days until
       the mouse is released"*. Those two fields were `weeks × rate` computed
       here, which cannot see a member's day rows, so the figure on screen for
       the whole gesture was the one the save would NOT produce. The page now
       costs the draft through `POST /crew-members/:id/cost-preview` — the real
       assembly — and hands the answer back down on the member row.
       🚨 **Do not re-add a money field to this callback.** This component
       cannot see a member's premium rates or their allowance caps, so anything
       it computed here would be a fifth cost formula in the one subsystem whose
       founding rule is that the server computes the money. */
    if (onMemberDragPreview) {
      onMemberDragPreview(dragState.member.id, { start_date: newStartStr, end_date: newEndStr });
    }
  }, [dragState, pxToDays, onMemberDragPreview]);

  const handleDragEnd = useCallback(() => {
    if (!dragState) return;
    const { member, handle, currentStart, currentEnd, originalStart, originalEnd } = dragState;
    const gesture = pointerGesture.current;
    pointerGesture.current = null;

    setDragState(null);

    /* A motionless or sub-radius BODY press is a CLICK and collapses (2026-10-05);
     * anything else writes the dates only if they truly moved — a drag out and
     * back to the origin writes nothing and does not collapse. */
    const datesMoved = currentStart !== originalStart || currentEnd !== originalEnd;
    // 🔀 2026-10-05: a clean BODY click collapses (block above the constants).
    if (handle === 'body' && !datesMoved && !(gesture && gesture.exceededThreshold)) {
      if (onDayEditToggle) {
        suppressBarClickRef.current = true;
        setTimeout(() => { suppressBarClickRef.current = false; }, 0);
        onDayEditToggle(member);
      }
      return;
    }
    if (!datesMoved) return;
    if (onMemberUpdate) {
      onMemberUpdate(member.id, { start_date: currentStart, end_date: currentEnd });
    }
  }, [dragState, onMemberUpdate, onDayEditToggle]);

  // Global drag listeners
  useEffect(() => {
    if (!dragState) return;
    const onMove = (e) => handleDragMove(e);
    const onEnd = () => handleDragEnd();

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = dragState.handle === 'body' ? 'grabbing' : 'col-resize';

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [dragState, handleDragMove, handleDragEnd]);

  /* ── THE PLACEHOLDER DRAG — commits Mon–Fri, not the dragged pixels ──────
   * Whole-block move only (no left/right resize handle — there is nothing to
   * resize before a member has a first engagement). The block visually
   * follows the pointer in whole-day steps; on release the landing date is
   * snapped BACKWARD to the Monday of whichever week it fell in
   * (`weekMondayOf`, not `firstVisibleMonday` — once dragged, the target
   * week can legitimately be off the chart's left edge if the user drags
   * that far, unlike the initial placement). A motionless press-and-release
   * writes nothing, matching "a bar click does nothing" for dated members. */
  const handlePlaceholderDragStart = useCallback((e, member) => {
    if (!canEdit) return;
    e.preventDefault();
    e.stopPropagation();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setPlaceholderDrag({ memberId: member.id, startX: clientX, deltaDays: 0 });
  }, [canEdit]);

  const handlePlaceholderDragMove = useCallback((e) => {
    if (!placeholderDrag) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const deltaDays = pxToDays(clientX - placeholderDrag.startX);
    if (deltaDays === placeholderDrag.deltaDays) return;
    setPlaceholderDrag(prev => ({ ...prev, deltaDays }));
  }, [placeholderDrag, pxToDays]);

  const handlePlaceholderDragEnd = useCallback(() => {
    if (!placeholderDrag) return;
    const { memberId, deltaDays } = placeholderDrag;
    setPlaceholderDrag(null);
    if (deltaDays === 0) return; // motionless press-and-release: no write
    const monday = weekMondayOf(addDays(placeholderWeekStart, deltaDays));
    const friday = addDays(monday, 4);
    if (onMemberUpdate) {
      onMemberUpdate(memberId, { start_date: toDateStr(monday), end_date: toDateStr(friday) });
    }
  }, [placeholderDrag, placeholderWeekStart, onMemberUpdate]);

  useEffect(() => {
    if (!placeholderDrag) return;
    const onMove = (e) => handlePlaceholderDragMove(e);
    const onEnd = () => handlePlaceholderDragEnd();
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [placeholderDrag, handlePlaceholderDragMove, handlePlaceholderDragEnd]);

  // Compute drag bar position — a COPY of `getBarStyle`'s geometry for the
  // in-flight drag frame; the +1 must match it (see the comment there).
  function getDragBar(member) {
    if (!dragState || dragState.member.id !== member.id) return null;
    const from = parseDate(dragState.currentStart);
    const to = parseDate(dragState.currentEnd);
    if (!from || !to) return null;
    const startDay = daysDiff(timelineStart, from);
    const duration = Math.max(daysDiff(from, to) + 1, 1);
    return {
      left: startDay * dayWidth,
      width: Math.max(duration * dayWidth, 4),
    };
  }

  // Drag tooltip
  const dragTooltipContent = dragState ? (() => {
    const { currentStart, currentEnd, originalStart, originalEnd, member } = dragState;
    const changed = currentStart !== originalStart || currentEnd !== originalEnd;

    // Determine action
    const totalDaysNew = daysDiff(parseDate(currentStart), parseDate(currentEnd));
    const totalDaysOrig = daysDiff(parseDate(originalStart), parseDate(originalEnd));
    let action, isExtend, isShrink;
    if (totalDaysNew > totalDaysOrig) {
      action = 'EXTEND'; isExtend = true; isShrink = false;
    } else if (totalDaysNew < totalDaysOrig) {
      action = 'SHORTEN'; isExtend = false; isShrink = true;
    } else {
      action = 'NO CHANGE'; isExtend = false; isShrink = false;
    }

    /* 🔻 THE `Budget: <original> → <live>` LINE IS GONE, 2026-08-23.
       `liveBudget` was `weeks × rate` computed right here, and it is the number
       the owner reported as wrong: *"when dragging the totals update but seems
       to not calculate the off days until the mouse is released"*. It could not
       see the member's day rows, so a member with a recorded 4-day week was
       previewed at 5. The live figure now comes from the server through the
       page and is READ off the member row — see `liveMember` below. */
    const liveMember = members.find((x) => x.id === member.id) || member;

    return {
      name: member.name,
      newStart: formatDateShort(currentStart),
      newEnd: formatDateShort(currentEnd),
      originalStart: formatDateShort(originalStart),
      originalEnd: formatDateShort(originalEnd),
      changed,
      action,
      isExtend,
      isShrink,
      /* 🔑 READ, NEVER DERIVED. `liveMember.budget_cost` is whatever the
         page currently holds for this member — the server's saved figure, or
         its `cost-preview` answer for the draft once that lands. There is no
         arithmetic here and there must not be. */
      liveBudget: liveMember.budget_cost,
      costState: liveCostStateFor(member.id),
    };
  })() : null;

  // 🔻 ONLY the true empty state — no dated members AND no placeholders —
  // short-circuits here. A department with dateless members alone now falls
  // through to the full chart so their placeholder blocks can draw; that is
  // the entire point of this feature (see the block above
  // `firstVisibleMonday`).
  if (validMembers.length === 0 && membersWithoutDates.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400 italic">
        No crew members to display.
      </div>
    );
  }

  return (
    /* 🧱 `isolate` — THE CHART IS ITS OWN STACKING CONTEXT (2026-09-23). The
       date band is `z-30` (`GANTT_AXIS_Z`) so it beats the panel and today
       marker INSIDE the chart; without a context here that z-30 competes at
       PAGE level and, measured on a real render at 1440, painted over the
       Toolbar (z-20) AND the app bar (`Layout.js`, z-30, earlier in the DOM)
       when the page scrolled the chart under them. The hover tooltip (`fixed
       z-50`) still paints above the chart content.
       🔑 `isolate` fixes the band vs the PAGE; inside the chart the band must
       also beat a DRAGGED bar, which is why `GANTT_DRAG_Z` (27) sits strictly
       below `GANTT_AXIS_Z` (30) — measured 2026-09-23, both repro paths. */
    <div data-testid="crew-gantt-root" className={`isolate bg-white dark:bg-gray-800 ${flush ? '' : 'rounded-lg border border-gray-200 dark:border-gray-700'}`}>
      {/* Controls bar — ONE ROW (2026-10-05, owner: desktop Labour Flow
          tightening). The legend used to be a sibling the row's `flex-wrap`
          dropped onto a second line whenever it did not fit beside Sort/Zoom
          (most departments: ~75px for two rows). It is now `flex-1 min-w-0`:
          a zero flex-basis can never force the row to wrap, so the legend
          takes whatever is left, right-aligned, and wraps its OWN chips if it
          must; `py-2` (was `py-3`) to match the toolbar above it.
          Sort/Zoom stay in this component — they are its own state
          (`zoom`, the sort keys) and the single-member chart shares it. */}
      {controlsTarget === undefined ? (
      <div className={`flex flex-wrap items-center justify-between gap-3 px-4 py-2 border-b border-gray-200 dark:border-gray-700 ${flush ? '' : 'rounded-t-lg'}`} data-section-title>
        <div className="flex items-center gap-4 flex-shrink-0">
          {/* Sort controls */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Sort</span>
            <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
              {['name', 'date'].map(s => (
                <button
                  key={s}
                  onClick={() => pickSort(s)}
                  className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                    sortBy === s
                      ? 'bg-blue-600 dark:bg-blue-500/70 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <button
              onClick={flipSortDir}
              className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              data-chevron
              title={sortDirTitle}
            >
              <svg className={`w-4 h-4 transition-transform ${sortDir === 'desc' || sortDir === 'end' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
              </svg>
            </button>
          </div>

          {/* Zoom controls */}
          <div className="flex items-center gap-2">
            <GanttZoomControl zoom={zoom} onZoom={zoomTo} />
          </div>
        </div>

        {/* Legend */}
        <div className="flex-1 min-w-0 flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-xs text-gray-700 dark:text-gray-200" data-testid="crew-gantt-legend">
          {grades.map((g, i) => {
            const color = GRADE_COLORS[i % GRADE_COLORS.length];
            return (
              <span key={g.id} className="flex items-center gap-1">
                <span className={`w-3 h-3 rounded ${color.bg} inline-block`}></span>
                {g.name}
              </span>
            );
          })}
          <span className="flex items-center gap-1 ml-1">
            {/* ⚠️ THE SWATCH MATCHES THE BAR'S WEIGHT, NOT ITS HUE — owner-approved
                2026-08-21 ("you can make it match if you think it will look
                better"). A real off week is the GRADE hex at `opacity: 0.15`
                with a dashed border; this read at `opacity-50`, i.e. more than
                three times as solid as the thing it claims to describe.
                🔑 It stays GREY deliberately: the legend is grade-agnostic and
                there is no one grade colour it could honestly take. Only the
                opacity and the dashed border are shared — and the 0.15 is
                literal rather than `opacity-50`-ish so the two move together if
                the bar's ever changes. */}
            <span className="w-3 h-3 rounded bg-gray-400 inline-block opacity-[0.15]" style={{ border: '1px dashed #9CA3AF' }}></span>
            <span className="text-gray-500 dark:text-gray-400">0 days</span>
          </span>
        </div>

        {/* 🔻 ONLY shown at `quarter` zoom now (2026-08-27) — at week/month
            the placeholder blocks below make this note redundant AND wrong
            ("hidden" is no longer true). Quarter suppresses the blocks
            themselves (see the placeholder-bar block further down for why),
            so this is the one zoom where the count is the only signal. */}
        {placeholderRowsVisible === false && membersWithoutDates.length > 0 && (
          <span className="text-xs text-amber-600 dark:text-amber-400">
            {membersWithoutDates.length} member(s) without dates hidden at this zoom — switch to Week or Month
          </span>
        )}
      </div>
      ) : (
        /* 🧰 `controlsTarget`: no controls row and no legend (owner, 2026-10-05).
           The quarter-zoom note is the one piece of that row with no other
           carrier, so it keeps a slim line here — and only when it applies. */
        placeholderRowsVisible === false && membersWithoutDates.length > 0 ? (
          <div className="px-4 py-1.5 border-b border-gray-200 dark:border-gray-700 text-xs text-amber-600 dark:text-amber-400" data-testid="crew-gantt-hidden-note">
            {membersWithoutDates.length} member(s) without dates hidden at this zoom — switch to Week or Month
          </div>
        ) : null
      )}
      {controlsTarget ? createPortal((
        /* The SAME state and handlers as the row above, drawn in the toolbar's
           segmented weight (`ui/Toolbar`) beside the Table/Gantt tabs. */
        /* Three distinct groups on roomier screens (owner 2026-10-05): View ·
           Sort · Zoom get a wider gap from 1400px up; tighter screens keep the
           standard gap (the bar already wraps at 1280 with the sidebar open). */
        <div className={`${TOOLBAR_GROUP} min-[1400px]:gap-x-6 min-[1400px]:ml-4`} data-testid="crew-gantt-toolbar-controls">
          {/* Hairline separators between the groups (owner 2026-10-05), the
              `ui/PageHeaderBand` hairline's own classes; shown where the groups
              are spaced (1400px+) so a wrapped line never starts with one. */}
          <span aria-hidden="true" className={GANTT_TOOLBAR_HAIRLINE} />
          {/* 🔀 No separate direction button (owner 2026-10-05: "clicking on
              Name or Date cycle through the ordering"). Clicking the ACTIVE
              segment flips it: Name A→Z ⇄ Z→A (▲/▼ shown), Date start ⇄ end
              (the segment reads "Start" / "End" — date order is always
              earliest-first, so the cycle is WHICH date, not a direction). */}
          <div className={TOOLBAR_SEGMENT_TRACK_COMPACT} role="group" aria-label="Sort">
            {['name', 'date'].map(s => {
              const active = sortBy === s;
              const label = s === 'date'
                ? (active && sortDir === 'end' ? 'End' : 'Start')
                : 'Name';
              const nextHint = s === 'date'
                ? (active ? (sortDir === 'end' ? 'click for start date' : 'click for end date') : 'sort by start date')
                : (active ? (sortDir === 'desc' ? 'click for A → Z' : 'click for Z → A') : 'sort A → Z');
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => (active ? flipSortDir() : pickSortFresh(s))}
                  aria-pressed={active}
                  className={`${TOOLBAR_SEGMENT_COMPACT} ${active ? TOOLBAR_SEGMENT_ON : TOOLBAR_SEGMENT_OFF}`}
                  title={`${active ? sortDirTitle : `Sort by ${s}`} — ${nextHint}`}
                >
                  {label}
                  {s === 'name' && active && (
                    /* Thin OUTLINE chevron, not a solid ▲ (owner 2026-10-05:
                       "chevron is too bold make it just the outline"). */
                    <svg aria-hidden="true" className={`inline-block ml-1 w-3 h-3 align-[-1px] ${sortDir === 'desc' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
          <span aria-hidden="true" className={GANTT_TOOLBAR_HAIRLINE} />
          <div className={TOOLBAR_SEGMENT_TRACK_COMPACT} role="group" aria-label="Zoom">
            {['week', 'month', 'quarter'].map(z => (
              <button
                key={z}
                type="button"
                onClick={() => zoomTo(z)}
                aria-pressed={zoom === z}
                className={`${TOOLBAR_SEGMENT_COMPACT} capitalize ${zoom === z ? TOOLBAR_SEGMENT_ON : TOOLBAR_SEGMENT_OFF}`}
              >
                {z}
              </button>
            ))}
          </div>
        </div>
      ), controlsTarget) : null}

      {/* Gantt body */}
      {/* 📐 THE CAP IS RAISED BY EXACTLY THE OPEN PANEL'S HEIGHT — a ruling, not
          a side effect. See `DETAILS_PANEL_PLACEMENT`. Inline means the panel
          takes room inside this box, and at 600px flat a 380px panel leaves 220px
          of the chart it belongs to. Adding its height back keeps the ROW
          viewport at exactly the 600px it has always been and lets the card grow,
          so the PAGE scrolls — which is what the crew table already does when a
          row expands. `detailsHeight` is 0 whenever the panel is shut, so this is
          literally `600px` for every user who never opens one.

          🔻 CORRECTED 2026-08-23 — THIS DESCRIBED A MECHANISM THAT HAD NEVER
          ONCE RUN, and the same is true of the `rowHeightFor` growth and the
          `detailsTop` offset that depend on the same value. `detailsHeight` is
          `detailsBodyHeight || 0`, and `detailsBodyHeight` was permanently
          `null`: `useMeasuredHeight` attached its ResizeObserver in a
          mount-time effect, when the conditionally-rendered panel node did not
          yet exist, so it never measured anything. Measured in Chromium before
          the fix: this cap sat at a flat 600px with a panel open, the timeline
          row stayed 86px instead of 590, and the panel was painted ON TOP OF
          the rows below it (rows at y=601/643/685 under a panel spanning
          601→1105). The hook is fixed and the behaviour above is now real.
          Recorded rather than quietly corrected: a comment asserting a
          mechanism that has never executed is worse than no comment, because
          it is the thing the next person trusts instead of measuring. */}
      {/* 🔻 THE ONE VERTICAL SCROLLER, holding BOTH columns — hoisted here on
          2026-08-23 so the level-2 panel can span them. `relative` is
          load-bearing: it is the panel's containing block. See
          `DETAILS_PANEL_PLACEMENT` before changing any of this.
          🪤 It used to be `flex overflow-hidden`, with the two columns each
          owning their own vertical scroll and a hand-written `scrollTop` sync
          between them. */}
      <div
        ref={bodyScrollRef}
        data-testid="crew-gantt-body-scroller"
        className={pageScroll ? 'relative' : 'relative overflow-y-auto'}
        style={pageScroll ? undefined : { maxHeight: 600 + detailsHeight }}
      >
        {/* 📌 THE STICKY DATE BAND — the body scroller's FIRST child, so it pins
            against the box that actually scrolls vertically. Opaque (rows scroll
            under it), exactly `headerHeight` in flow (the panel's `top` adds it
            once), `GANTT_AXIS_Z` so it beats the today marker (z-20) and the
            panel (z-25). 🚫 Never move it back inside the timeline: there a
            `sticky top-0` is inert, because the timeline is x-only. */}
        <div
          ref={dateBandRef}
          data-testid="crew-gantt-date-band"
          /* `bg-*`: the edge fade masks the clip, and what shows through must be the card, never rows scrolling under the pinned band. */
          className={`sticky top-0 ${GANTT_AXIS_Z} flex cursor-grab bg-white dark:bg-gray-800`}
          style={pageScroll ? { height: headerHeight, top: stickyTop } : { height: headerHeight }}
          onWheel={handleBandWheel}
        >
          <div
            data-testid="crew-gantt-date-band-corner"
            className="flex-shrink-0 border-r border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 flex items-center"
            style={{ width: labelWidth }}
          >
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Member
            </span>
          </div>
          <div
            ref={dateAxisClipRef}
            data-testid="crew-gantt-date-band-clip"
            className="flex-1 min-w-0 overflow-hidden"
            style={timelineFadeStyle}
          >
            <GanttDateAxis
              testId="crew-gantt-date-axis"
              className="relative"
              style={{ width: totalWidth, minWidth: '100%' }}
              dateHeaders={dateHeaders}
              dayWidth={dayWidth}
              height={headerHeight}
            />
          </div>
        </div>
        <div className="flex">
        {/* Fixed left label column */}
        <div className="flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900" style={{ width: labelWidth }}>
          <div data-crew-gantt-labels ref={labelsRef}>
            {validMembers.map(m => (
              <div
                key={m.id}
                data-row-moved={m.id === movedRowId ? 'true' : undefined}
                className={`flex flex-col justify-start border-b border-gray-100 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300 ${ROW_EXPAND_TRANSITION}`}
                style={{ height: rowHeightFor(m) }}
              >
              <div
                className={`flex items-center px-3 truncate transition-colors ${
                  canExpandRows ? `cursor-pointer ${ROW_EXPAND_HOVER}` : ''
                } ${m.id === movedRowId ? 'duration-500 bg-blue-100 dark:bg-blue-900/50' : ''}`}
                style={{ height: rowHeight }}
                title={`${m.name} — ${m.grade_name || 'No grade'}`}
                data-testid={`crew-gantt-member-row-${m.id}`}
              >
                {/* ⚠️ THE CHEVRON AND THE ROW SURFACE ARE TWO CONTROLS DOING
                    THE SAME THING — SIBLINGS, NEVER NESTED. Owner, 2026-08-22:
                    *"clicking the whole member row not just the chevron should
                    expand the row."* So the row gained a second entry point to
                    the one action, and the chevron stayed rather than being
                    absorbed: it is the affordance that SAYS the row expands,
                    and it is what carries `aria-expanded` for a screen reader
                    that lands on it first.
                    🪤 THEY MUST STAY SIBLINGS. Wrapping the chevron inside the
                    row button would nest a control inside a control — invalid
                    ARIA, and the exact "two controls in one visual label"
                    hazard the old chevron/name pair was built to avoid. §1.1 of
                    `CrewGanttView.dayToggle.test.js` pins the `contains` test in
                    both directions.
                    🔻 AND THE NAME IS NO LONGER A BUTTON. It used to be the
                    route to the table view; that route is now the VIEW
                    SWITCHER alone (2026-08-24 — the `Full Details` button that
                    briefly held it is deleted, see the save bar below), so the
                    name is a plain `<span>`. Do not reintroduce a clickable
                    name — a third control in this label is what the
                    two-controls rule forbids, and the deletion below removed a
                    navigation from this row rather than relocating one. */}
                <button
                  type="button"
                  onClick={dayEditAvailable && dayEditingAllowed ? () => onDayEditToggle(m) : undefined}
                  disabled={!!dayEditDisabledReason}
                  aria-expanded={dayEditMemberId === m.id}
                  aria-label={`${dayEditMemberId === m.id ? 'Hide' : 'Show'} worked days for ${m.name}`}
                  title={dayEditDisabledReason || `${dayEditMemberId === m.id ? 'Hide' : 'Show'} worked days`}
                  data-testid={`crew-gantt-day-chevron-${m.id}`}
                  className="mr-1 -ml-0.5 p-1 rounded flex-shrink-0 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                  <svg
                    className={`w-3.5 h-3.5 transition-transform ${dayEditMemberId === m.id ? 'rotate-90' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                {/* THE ROW SURFACE — the second entry point to the expand.
                    A real `<button>`, so it is a tab stop, has a focus ring and
                    gets Enter/Space key handling from the platform rather than
                    from a hand-rolled `onKeyDown` on a `div`. It carries
                    `aria-expanded` too: it genuinely controls the same region
                    the chevron does, and two triggers each announcing the state
                    is correct ARIA — what is forbidden is one control claiming
                    an expansion it does not perform.
                    ⚠️ `disabled` rather than absent when the row cannot expand
                    (quarter zoom, or no permission): a control that vanishes
                    reads as a bug, and this mirrors the chevron beside it. */}
                <button
                  type="button"
                  onClick={canExpandRows ? () => onDayEditToggle(m) : undefined}
                  disabled={!canExpandRows}
                  aria-expanded={dayEditMemberId === m.id}
                  aria-label={`${dayEditMemberId === m.id ? 'Collapse' : 'Expand'} ${m.name}`}
                  data-testid={`crew-gantt-member-surface-${m.id}`}
                  className="flex-1 min-w-0 flex items-center text-left rounded disabled:cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                  <span className={`w-2 h-2 rounded-full ${getGradeColor(m).bg} mr-1.5 flex-shrink-0`}></span>
                  {m.user_id ? (
                    <span className={`w-1.5 h-1.5 rounded-full mr-1.5 flex-shrink-0 ${
                      m.user_status === 'active' ? 'bg-green-400' :
                      m.user_status === 'invited' ? 'bg-yellow-400' :
                      m.user_status === 'deactivated' ? 'bg-red-400' :
                      'bg-gray-400'
                    }`} title={`User: ${m.user_status || 'placeholder'}`}></span>
                  ) : null}
                  {/* 🔻 A `<span>`, NOT A BUTTON — owner ruling, 2026-08-22. The
                      route to the table view is the VIEW SWITCHER (the
                      `Full Details` button that briefly carried it was deleted
                      on 2026-08-24).
                      🔻 AND IT CARRIES **NO** `title` OF ITS OWN — THE BRIEF FOR
                      THIS CHANGE ASKED FOR ONE AND IT WOULD BE A DEFECT. Its
                      words were *"The name keeps its `title` with the full
                      untruncated name"*, but the name never had one: the full
                      name has always lived on the ROW's `title`
                      (`<name> — <grade>`), and a native `title` on a CHILD wins
                      over its parent's — so adding one here would shadow the row
                      tooltip on exactly the pixels a user aims at, which is the
                      defect already recorded against `DueIndicator` and
                      `LeadTimeDot`. §8.2 of `CrewGanttView.dayToggle.test.js`
                      pins the absence and goes red if it is added. The brief's
                      GOAL — the untruncated name is recoverable on hover — is
                      already met by the row. */}
                  <span
                    className="min-w-0 truncate"
                    data-testid={`crew-gantt-member-name-${m.id}`}
                  >
                    {truncateCrewName(m.name)}
                  </span>
                  {/* 🔑 THE TOTAL PRICE, MOVED OFF THE BAR (owner ruling — see the
                      block where `BarLabel` stood).
                      🚨 IT IS READ, NEVER RE-DERIVED, AND SINCE 2026-08-23 THERE IS
                      NO EXCEPTION AT ALL. `m.budget_cost` is the server's figure
                      — `resolveEffectiveRate` honours `weekly_rate_override`, and
                      four cost formulas in this repo have already drifted apart
                      with one of them ignoring that column in production. While a
                      strip is open the page overlays this same field with the
                      answer `POST /crew-members/:id/cost-preview` gives for the
                      DRAFT, so a drag, a day toggle and a typed date all move it
                      through the real assembly.
                      🔻 IT USED TO READ `dragTooltipContent.liveBudget` MID-DRAG,
                      AND THAT WAS THE REPORTED DEFECT — that figure was
                      `weeks × rate` and could not see the member's day rows, so a
                      dragging user watched a number the save would not produce.
                      The tooltip now reads THIS field rather than the other way
                      round; there is one figure on the row and it is the server's.
                      🚨 `flex-shrink-0`: the price must never truncate. */}
                  <span
                    className="ml-auto pl-2 flex-shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400"
                    data-testid={`crew-gantt-member-price-${m.id}`}
                    data-cost-state={liveCostStateFor(m.id)}
                    title={liveCostTitleFor(m.id)}
                  >
                    {liveCostStateFor(m.id) === 'failed'
                      ? LIVE_COST_ABSENT_MARK
                      : formatMoney(m.budget_cost || 0)}
                    {liveCostStateFor(m.id) === 'pending' && (
                      <span aria-hidden="true">{LIVE_COST_PENDING_MARK}</span>
                    )}
                  </span>
                </button>
              </div>
              {/* The save bar lives in the FIXED label column, not out on the
                  timeline: the timeline scrolls horizontally, so a Save button
                  placed beside the toggles could be scrolled off screen while
                  the row is dirty. */}
              {/* The label side's Save/Cancel bar does NOT linger on close (see
                  `closingDayEditId`): a lingering Save is a control, not a picture. */}
              {dayEditMemberId === m.id && (
                <div
                  className="flex flex-col justify-center px-3"
                  style={{ height: DAY_TOGGLE_ROW_HEIGHT }}
                  data-testid={`crew-gantt-day-edit-bar-${m.id}`}
                >
                  {/* ═══ BUTTON ORDER — a decision, not the order of the JSX ═══
                      `components/ConfirmDialog.js`'s rule is that order is a
                      design decision; the same care applies here, and the
                      question this row poses is different from a dialog's.
                      Left to right: **Save · Cancel · Details**.
                      🔑 SAVE AND CANCEL KEEP THE LEFT, IN THE ORDER THEY
                      ALREADY SHIPPED. They are the strip's own business and the
                      pair the eye starts on; re-ordering a shipped pair to
                      accommodate a newcomer costs muscle memory for nothing.
                      🔻 THIS BAR NO LONGER HOLDS A NAVIGATION AT ALL (2026-08-24).
                      `Full Details` sat third and needed THREE separate devices
                      to stop reading as a way to commit — last position,
                      `ml-auto`, and a hand-spelt ghost weight `BTN_XS` has no
                      intent for. All three are gone with it. **What remains are
                      two commit controls and one disclosure**, which is a
                      coherent row rather than one held together by styling.
                      🚫 So: **do not add a navigation to this bar.** If a route
                      to the table view is ever wanted from here again, the
                      question to answer first is why the view switcher is not
                      the place for it — that is the argument the deletion
                      settled. */}
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={onDayEditSave || undefined}
                      disabled={!dayEditDirty || dayEditSaving || !onDayEditSave}
                      data-testid={`crew-gantt-day-save-${m.id}`}
                      className={`${BTN_XS.confirm} disabled:opacity-40 disabled:cursor-not-allowed`}
                    >
                      {dayEditSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={onDayEditCancel || undefined}
                      disabled={dayEditSaving || !onDayEditCancel}
                      data-testid={`crew-gantt-day-cancel-${m.id}`}
                      className={`${BTN_XS.secondary} disabled:opacity-40 disabled:cursor-not-allowed`}
                    >
                      Cancel
                    </button>
                    {/* ── LEVEL 2 — THE SECOND EXPAND ──────────────────────
                        🔑 A THIRD CONTROL IN THIS BAR, AND IT IS A DISCLOSURE,
                        NOT A COMMIT AND NOT A NAVIGATION. It keeps the
                        SECONDARY weight so it reads as a peer of Cancel rather
                        than of the primary Save, and it carries `aria-expanded`
                        because that is exactly what it does.
                        🔻 IT IS NOW THE ONLY NON-COMMIT CONTROL IN THE BAR.
                        `Full Details` used to sit beside it and was deleted on
                        2026-08-24 — the two were similarly worded, which was a
                        real and acknowledged cost, paid for at the time by a
                        ghost weight and an `ml-auto` gap. With it gone this
                        button needs no such separation and keeps the SECONDARY
                        weight simply because it is a peer of Cancel.
                        ⚠️ RENDERED ONLY WHEN THE PAGE SUPPLIES A HANDLER, so a
                        caller that has not opted in (the single-member Gantt
                        inside the details panel — where a second details panel
                        would be a panel inside itself) sees the bar it always
                        saw. */}
                    {onDetailsToggle && (
                      <button
                        type="button"
                        onClick={() => onDetailsToggle(m)}
                        disabled={dayEditSaving}
                        aria-expanded={detailsMemberId === m.id}
                        title={detailsMemberId === m.id
                          ? `Hide ${m.name}'s details`
                          : `Show ${m.name}'s grade, rate, allowances and notes`}
                        data-testid={`crew-gantt-details-toggle-${m.id}`}
                        className={`${BTN_XS.secondary} disabled:opacity-40 disabled:cursor-not-allowed`}
                      >
                        {detailsMemberId === m.id ? 'Hide details' : 'Details'}
                      </button>
                    )}
                    {/* 🔻 `Full Details` WAS HERE AND IS DELETED (2026-08-24).
                        Owner: *"remove the 'Full Details' link from the
                        expanded gantt as the user gets to that page from the
                        table view."* It navigated to the crew TABLE, which is
                        reachable from the view switcher, so it was a second
                        route to a place that already had one — and it sat in a
                        bar of COMMIT buttons, which is why it needed three
                        separate devices (last position, `ml-auto`, ghost
                        weight) to stop reading as a third way to commit. The
                        cheapest fix for that was to not have it.
                        🚫 DO NOT RESTORE IT. The `Details` button next door is
                        a DISCLOSURE, not a navigation, and is the control this
                        bar is meant to carry — two similarly-worded controls on
                        one bar was the cost this deletion pays off.
                        🪤 `onMemberClick` WENT WITH IT (2026-08-24) — this was
                        its only caller, so the prop is gone from this
                        component's signature and the page's
                        `handleGanttMemberClick` is deleted with its two
                        prop-pass sites, closing exactly the dead-scaffolding
                        class the 2026-08-23 code review flagged five
                        instances of. `guardGanttDayEdit` and `membersRef`,
                        which that handler called into, are NOT part of this —
                        both have other live callers (the Details toggle, the
                        day-strip open/close) and stay. */}
                  </div>
                  {/* 🔻 ON ITS OWN LINE, AND THE `truncate` IS GONE WITH IT —
                      owner, 2026-08-22: the label was clipping, and *"put
                      unsaved changes underneath if it helps with the UI"*.
                      🪤 THE SOURCE STRING WAS `Unsaved`, NOT `Unsaved changes`
                      — it was reported as "cut off" because a bare `Unsaved`
                      beside three buttons reads as a truncated phrase, and
                      `truncate` made that reading credible. Fixing it by
                      shortening the label would have been fixing the symptom of
                      a layout problem; the label now has a row of its own and
                      says the whole phrase.
                      📏 The strip is a FIXED `DAY_TOGGLE_ROW_HEIGHT` (44px) and
                      that height is load-bearing — `rowHeightFor` derives every
                      row height, the background grid, the today marker and the
                      toggle strip's own `top` from it. **It is UNCHANGED.** The
                      stack fits: `BTN_XS` is `py-1 text-xs` ≈ 26px, plus a
                      `text-[10px]` line ≈ 12px, = 38px inside 44px.
                      🪤 jsdom has NO LAYOUT, so nothing in any test can prove
                      this no longer clips — a test can only prove the string is
                      what was intended and that `truncate` is gone. Whether it
                      fits is a BROWSER check and the owner's to judge.
                      🎨 `text-amber-700 dark:text-amber-400` is the measured
                      house pair for "caution, nothing is broken" in this
                      directory (`UNPRICED_PREMIUM_CLASS`, `InactiveBadge.js`) —
                      kept exactly. */}
                  {dayEditDirty && (
                    <span
                      className="text-[10px] font-medium leading-tight text-amber-700 dark:text-amber-400"
                      data-testid={`crew-gantt-day-unsaved-${m.id}`}
                    >
                      Unsaved changes
                    </span>
                  )}
                </div>
              )}
              </div>
            ))}
            {/* ── THE "NO DATES SET" PLACEHOLDER ROWS — label column half ──
                Deliberately plain: no chevron, no day-edit affordance. A
                dateless member has no server-resolved `days` and no real
                week to walk `generateWeekSegments` over, so the day-toggle
                strip is not reachable here — see `noDatesSetTitle` above for
                the full reasoning. The only control is the bar itself, in
                the timeline column, which drags to commit real dates. */}
            {placeholderRowsVisible && membersWithoutDates.map(m => (
              <div
                key={`placeholder-label-${m.id}`}
                className="flex items-center px-3 border-b border-gray-100 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400"
                style={{ height: rowHeight }}
                title={`${m.name} — ${m.grade_name || 'No grade'} — no dates set`}
                data-testid={`crew-gantt-placeholder-label-${m.id}`}
              >
                <span
                  className={`w-2 h-2 rounded-full border ${getGradeColor(m).bg} opacity-50 mr-1.5 flex-shrink-0`}
                  aria-hidden="true"
                ></span>
                <span className="min-w-0 truncate">{truncateCrewName(m.name)}</span>
                <span className="ml-auto pl-2 flex-shrink-0 text-xs italic text-gray-400 dark:text-gray-500">
                  No dates
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* HORIZONTALLY scrollable timeline — panning handled by useDragScroll above */}
        {/* 🔑 A STABLE HANDLE FOR **THE HORIZONTAL SCROLL CONTAINER ITSELF**,
            added 2026-08-22. The level-2 details panel must not be inside this
            element (see `DETAILS_PANEL_PLACEMENT`), and that is the one claim
            about the panel jsdom CAN check — but only if the container can be
            located without asking what CSS it happens to carry. Locating the bar
            by `style*="cursor: grab"` already cost five red tests on this file
            when the cursor changed; do not repeat it one level up.
            🔻 `overflow-auto` → `overflow-x-auto` on 2026-08-23: the VERTICAL
            axis moved out to `crew-gantt-body-scroller`, which is what lets the
            panel span both columns and is what deleted the `scrollTop` sync that
            used to live in this `onScroll`. Do not put `overflow-y` back — two
            vertical scrollers is the desync this change removed. The remaining
            `overflow-x-auto` is ALSO what keeps this flex item's `min-width:
            auto` resolving to 0, so the 29,000px content cannot widen the body
            scroller and drag the panel's `right: 0` off screen. */}
        <div
          className={pageScroll ? `flex-1 ${PAGE_SCROLL_PANE_X} cursor-grab` : 'flex-1 overflow-x-auto cursor-grab'}
          data-testid="crew-gantt-timeline-scrollport"
          ref={timelineRef}
          style={timelineFadeStyle}
          onScroll={() => {
            readTimelineFade();
            // Re-measure the horizontal window so the day toggles stay bounded
            // to what is on screen. `measureScrollView` no-ops when nothing
            // moved, so this cannot loop.
            measureScrollView();
          }}
        >
          <div style={{ width: totalWidth, minWidth: '100%' }}>
            {/* 🔻 The date header is NOT here any more — it is the sticky date
                band above, first child of the body scroller. */}

            {/* Chart rows */}
            <div className="relative">
              {/* Background grid */}
              <GanttGrid dateHeaders={dateHeaders} dayWidth={dayWidth} height={chartHeight} />
              <GanttTodayLine todayOffset={todayOffset} totalDays={totalDays} dayWidth={dayWidth} height={chartHeight} lineRef={todayLineRef} />

              {/* Member bars */}
              {validMembers.map(member => {
                const bar = getBarStyle(member);
                const dragBar = getDragBar(member);
                const colors = getGradeColor(member);
                const isDragging = dragState && dragState.member.id === member.id;
                const displayBar = isDragging && dragBar ? dragBar : bar;
                const dragMember = isDragging ? { ...member, start_date: dragState.currentStart, end_date: dragState.currentEnd } : member;
                // 🔑 ONE RESOLUTION, TWO SURFACES. The set the page resolved
                // through `utils/crewDayResolution.js` drives the bar's fade
                // AND the toggle strip below it, because they are the same
                // `dayCells` array read twice. It applies ONLY to the member
                // being edited — everyone else keeps the shipped default.
                const isDayEditing = member.id === dayEditMemberId;
                // 🔻 A COLLAPSED BAR IS A PAN SURFACE AND NOTHING ELSE — owner
                // ruling, 2026-08-22. `barExpandsOnClick` stood here and is
                // DELETED with the click handler it gated; see the block on the
                // bar element below. `canDragMember`'s docblock covers why the
                // zoom term is not redundant with the expanded term.
                const barIsDraggable = canDragMember(member);
                const segments = generateWeekSegments(
                  dragMember, timelineStart, dayWidth,
                  isDayEditing ? dayEditWorkedDates : null
                );
                const barHeight = rowHeight - 12;
                // For drag preview: compute original bar pixel bounds to highlight new segments
                const origStart = isDragging ? parseDate(dragState.originalStart) : null;
                const origEnd = isDragging ? parseDate(dragState.originalEnd) : null;

                return (
                  <div
                    key={member.id}
                    /* The filter is applied HERE, on the timeline row, and
                       deliberately NOT on the label column: the label's text
                       is already achromatic, so greying it would change
                       exactly one thing — the GRADE DOT beside the name — and
                       grade would be left with a hover-only `title` as its
                       sole channel on every inactive row. */
                    className={`relative border-b border-gray-100 dark:border-gray-700 ${ROW_EXPAND_TRANSITION}`}
                    data-testid={`crew-gantt-timeline-row-${member.id}`}
                    style={{
                      height: rowHeightFor(member),
                      ...(isRowGreyed(member.id) ? { filter: INACTIVE_ROW_FILTER } : {}),
                    }}
                  >
                    {displayBar && (
                      <div
                        /* 🔑 A STABLE HANDLE FOR THE BAR. Every test located it
                           by `div[style*="cursor: grab"]` / `pointer` /
                           `default`, so the bar could only be found by asking
                           what MODE it was in — and the moment the collapsed
                           bar's cursor changed (pan ruling, 2026-08-22) five
                           tests went red for a locator reason while the
                           behaviour they assert was correct. Locate by this;
                           assert the cursor only where the cursor IS the claim. */
                        data-testid={`crew-gantt-bar-${member.id}`}
                        className={`absolute top-1.5 rounded-md overflow-hidden ${colors.text} ${isDragging ? 'shadow-lg' : 'shadow-sm hover:shadow-md'} ${member.id === movedRowId ? 'crew-gantt-bar-moved' : ''}`}
                        style={{
                          left: displayBar.left,
                          width: displayBar.width,
                          height: barHeight,
                          zIndex: isDragging ? GANTT_DRAG_Z : 10,
                          // 🔑 THE CURSOR IS THE ONLY THING THAT SAYS WHICH OF
                          // THE BAR'S TWO MODES IS LIVE. `move` (it was `grab` until 2026-10-11) once the row is
                          // expanded (the bar owns the gesture and moves dates);
                          // `inherit` while collapsed, so the timeline's own
                          // `cursor-grab` reads through and the bar advertises
                          // the PAN it now participates in. It was `pointer`
                          // while collapsed until the pan ruling below — a
                          // pointer cursor over a surface whose click does
                          // nothing is a promise the bar no longer keeps.
                          // 🔀 2026-10-05: a collapsed bar's click EXPANDS it now
                          // (`onClick` below), so the premise above expired —
                          // it shows `pointer` wherever that click works, like
                          // the hires bars (owner: *"on the dept hires gannt the
                          // mouse changes when it hovers over a bar but NOT on
                          // the … labour flow gannt?"*). A drag still pans.
                          // 🔀 2026-10-11: a bar that MOVES shows `move`, never the
                          // background's `grab` (`constants/ganttCursors.js`).
                          cursor: barIsDraggable ? GANTT_CURSOR.move : (canExpandRows && member.id !== dayEditMemberId ? GANTT_CURSOR.open : 'inherit'),
                        }}
                        onMouseEnter={(e) => showTooltip(e, member)}
                        onMouseLeave={hideTooltip}
                        /* 🔻 PAN-SKIP IS CONDITIONAL, AND THIS ATTRIBUTE WAS THE
                           DEFECT. Owner, on the staging build: *"we still have
                           an issue where when scrolling around the gantt chart
                           the user wants to click and drag to scroll the chart
                           NOT the individual bars. So actually clicking on the
                           bar should do nothing apart from let us scroll the
                           entire chart."*
                           `useDragScroll`'s `mousedown` opens with
                           `e.target.closest('button, a, …, [data-pan-skip]')`
                           and RETURNS — so while this was unconditional a pan
                           could not even START on a bar, and bars cover most of
                           the timeline. That is the larger half of what he
                           reported: not merely that the click had a side
                           effect, but that the gesture he wanted was refused
                           outright. The bar is a `<div>`, not a `<button>`, so
                           `data-pan-skip` was the whole of the block.
                           EXPANDED it must stay: a date drag that also scrolled
                           the chart under the pointer would fight itself. */
                        {...(barIsDraggable ? { 'data-pan-skip': '' } : {})}
                        /* 🔀 A COLLAPSED BAR'S CLICK EXPANDS IT — REVISED
                           2026-10-05. It was ruled click-does-nothing on
                           2026-08-22 (*"clicking on the bar should do nothing
                           apart from let us scroll the entire chart"*); owner,
                           2026-10-05: *"like the other gannt charts clicking the
                           bar should expand it"*. Both halves now hold: a DRAG
                           still pans (no `data-pan-skip` while collapsed, and
                           `useDragScroll` swallows the click a real pan
                           produces), and a plain CLICK calls the SAME
                           `onDayEditToggle` the label row and chevron call —
                           same page dirty-guard, same zoom gate
                           (`canExpandRows`; quarter does nothing).
                           The EXPANDED bar has no `onClick` (the
                           `dayEditMemberId` term): its clean click COLLAPSES
                           through `handleDragEnd` (2026-10-05, *"clicking a
                           gannt bar a second time should close it"*), and
                           `suppressBarClickRef` stops the trailing click
                           re-expanding the now-collapsed bar. */
                        onClick={!barIsDraggable && canExpandRows && member.id !== dayEditMemberId ? () => { if (!suppressBarClickRef.current) onDayEditToggle(member); } : undefined}
                        onMouseDown={(e) => barIsDraggable && handleDragStart(e, member, 'body')}
                        onTouchStart={(e) => barIsDraggable && handleDragStart(e, member, 'body')}
                      >
                        {/* Week segments with opacity */}
                        {segments.map((seg, segIdx) => {
                          // 🔑 `isOff` is UNCHANGED — still driven by the day
                          // count (or the allocation fraction when there is no
                          // day data), still the sole test for "OFF WEEKS keep
                          // their existing treatment". See `buildWorkedDateSet`'s
                          // block comment: an OFF week is a distinct tracked
                          // fact from "some days worked, some faded" and must
                          // stay visually distinguishable from it.
                          const shade = segmentShade(seg);
                          const isOff = shade === 0;
                          // During drag, detect changed segments: new (outside original range) or partial edge
                          const segDate = parseDate(seg.weekKey);
                          const isNewSegment = isDragging && segDate && (
                            (origEnd && segDate >= origEnd) || (origStart && segDate < origStart)
                          );
                          const isPartialEdge = isDragging && seg.days < 7 && (
                            segIdx === 0 || segIdx === segments.length - 1
                          );
                          const isChangedSegment = isNewSegment || isPartialEdge;
                          // Show day count label on partial/new segments during drag
                          const dragDayLabel = isDragging && isChangedSegment && seg.weekdays > 0 && seg.weekdays < 5
                            ? `${seg.weekdays} day${seg.weekdays > 1 ? 's' : ''}`
                            : null;
                          const label = getAllocationLabel(seg.allocation);
                          // 🔻 PER-DAY FADE (owner ruling, see the block
                          // comment above `buildWorkedDateSet`) on every
                          // non-off week. 🔀 2026-10-06 — INCLUDING MID-DRAG
                          // (owner: *"make the days populate live as we
                          // drag"*). It used to fall back to the old single
                          // rect (0.3/0.7) while dragging because the day set
                          // was resolved for the pre-drag range; the page now
                          // resolves `dayEditWorkedDates` over the LIVE drag
                          // frame (the same range it sends to cost-preview),
                          // so the cells it draws are the days being costed.
                          // Only the dragged member can be mid-drag, and it is
                          // always the day-edit member (a drag needs the row
                          // expanded), so the set always applies to it.
                          const showDayCells = !isOff;
                          return (
                            <React.Fragment key={seg.weekKey}>
                              {showDayCells ? (
                                seg.dayCells.map((cell) => (
                                  <div
                                    key={cell.date}
                                    className={colors.bg}
                                    style={{
                                      position: 'absolute',
                                      left: cell.left,
                                      width: cell.width,
                                      top: 0,
                                      height: barHeight,
                                      opacity: cell.worked ? WORKED_DAY_OPACITY : FADED_DAY_OPACITY,
                                    }}
                                  />
                                ))
                              ) : (
                                <div
                                  // Only an OFF week reaches here since 2026-10-06 (every
                                  // other week draws day cells, mid-drag included).
                                  className="border border-dashed rounded-sm"
                                  style={{
                                    position: 'absolute',
                                    left: seg.left,
                                    width: seg.width,
                                    top: 0,
                                    height: barHeight,
                                    opacity: isDragging ? (isChangedSegment ? 0.3 : 0.7) : 0.15,
                                    borderColor: isOff ? colors.hex : undefined,
                                    backgroundColor: isOff ? colors.hex : undefined,
                                  }}
                                />
                              )}
                              {((dragDayLabel && seg.width >= 20) || (seg.width >= 35 && !isDragging && seg.allocation < 1)) && (
                                <span
                                  className={`absolute flex items-center justify-center text-[10px] font-semibold pointer-events-none select-none whitespace-nowrap ${
                                    isOff ? 'text-gray-500 dark:text-gray-400' : colors.text
                                  }`}
                                  style={{
                                    left: seg.left,
                                    width: seg.width,
                                    top: 0,
                                    height: barHeight,
                                    textShadow: isOff ? 'none' : '0 1px 2px rgba(0,0,0,0.3)',
                                  }}
                                >
                                  {dragDayLabel || label}
                                </span>
                              )}
                            </React.Fragment>
                          );
                        })}

                        {/* ── 6th/7th-day premium marks ──────────────────────────
                            🔻 SUPPRESSED WHILE DRAGGING, deliberately. A date
                            drag moves the bar; it does NOT move the member's
                            day rows, which are recorded facts (the same reason
                            `previewWeeklyRate`'s docblock gives for the drag
                            preview disagreeing for a hand-recorded member). The
                            marks are positioned relative to `member.start_date`,
                            so drawing them against a bar whose origin has moved
                            would put them on the wrong days. They come back the
                            moment the drag ends.
                            🔑 Rendered AFTER the segments, so the marks sit
                            over the per-day fade rather than under it. (They
                            used to sit under `BarLabel` too; that component was
                            deleted on 2026-08-22 — owner ruling.) */}
                        {!isDragging && (premiumMarksByMember.get(member.id) || []).map(mark => (
                          <PremiumDayMark
                            key={`premium-${mark.date}`}
                            mark={mark}
                            barHeight={barHeight}
                            dayWidth={dayWidth}
                          />
                        ))}

                        {/* Left drag handle.
                            🔒 RENDERED ONLY WHEN THE ROW IS EXPANDED, not merely
                            hidden or made inert: the handle IS the affordance
                            that says "this edge moves", and showing one that
                            refuses to move is the silent-refusal failure the
                            exit-prompt work was written to remove. */}
                        {barIsDraggable && (
                          <div
                            className="absolute top-0 left-0 w-2 h-full cursor-col-resize group/lhandle flex items-center justify-center z-10"
                            style={{ marginLeft: -1 }}
                            onMouseDown={(e) => handleDragStart(e, member, 'left')}
                            onTouchStart={(e) => handleDragStart(e, member, 'left')}
                          >
                            <div className="w-1 h-3 rounded-sm bg-white/50 group-hover/lhandle:bg-white/90 transition-colors" />
                          </div>
                        )}


                        {/* Right drag handle — same gate, same reason. */}
                        {barIsDraggable && (
                          <div
                            className="absolute top-0 right-0 w-2 h-full cursor-col-resize group/rhandle flex items-center justify-center z-10"
                            style={{ marginRight: -1 }}
                            onMouseDown={(e) => handleDragStart(e, member, 'right')}
                            onTouchStart={(e) => handleDragStart(e, member, 'right')}
                          >
                            <div className="w-1 h-3 rounded-sm bg-white/50 group-hover/rhandle:bg-white/90 transition-colors" />
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── THE DAY TOGGLE STRIP ──────────────────────────────
                        🚨 A SIBLING OF THE BAR, NEVER A CHILD OF IT. The bar
                        owns three pointer gestures (left handle, right handle,
                        body) and a click-to-toggle laid on top of them is the
                        conflict this feature was warned about. Down here the
                        drag handlers are simply not on the path: a `mousedown`
                        on a toggle never reaches `handleDragStart`, and a drag
                        that begins on the bar ends in `handleDragEnd`, which
                        calls `onMemberUpdate` and nothing else. There is no
                        threshold to tune and no `stopPropagation` to remember.
                        ⚠️ It also renders while `isDragging`, and it must: the
                        toggles show the member's RECORDED days, which a date
                        drag does not move — the same reason the premium marks
                        suppress themselves against a bar whose origin has
                        shifted. */}
                    {(isDayEditing || member.id === closingDayEditId) && displayBar && (
                      <div
                        className={`absolute left-0 right-0 ${isDayEditing ? '' : 'pointer-events-none'}`}
                        style={{ top: rowHeight, height: DAY_TOGGLE_ROW_HEIGHT }}
                        aria-hidden={isDayEditing ? undefined : true}
                        data-testid={isDayEditing ? `crew-gantt-day-strip-${member.id}` : undefined}
                      >
                        {dayEditingAllowed ? (
                          segments.flatMap((seg) => seg.dayCells).map((cell) => {
                            const dayIdx = Math.round((displayBar.left + cell.left) / dayWidth);
                            if (visibleDayRange
                              && (dayIdx < visibleDayRange.first || dayIdx > visibleDayRange.last)) {
                              return null;
                            }
                            return (
                              <DayToggleButton
                                key={cell.date}
                                member={member}
                                cell={cell}
                                colors={colors}
                                left={displayBar.left + cell.left}
                                disabled={dayEditSaving || !onDayToggle}
                                onToggle={onDayToggle}
                                ghost={!isDayEditing}
                              />
                            );
                          })
                        ) : (
                          // Zoomed out with a strip already open. Collapsing it
                          // here would look exactly like the unsaved edit being
                          // thrown away, so the row stays and says why instead.
                          <span
                            className="absolute top-0 flex items-center text-xs italic text-gray-500 dark:text-gray-400 whitespace-nowrap"
                            style={{ left: displayBar.left, height: DAY_TOGGLE_ROW_HEIGHT }}
                            data-testid={`crew-gantt-day-strip-notice-${member.id}`}
                          >
                            {DAY_EDIT_DISABLED_TITLE}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* ── THE "NO DATES SET" PLACEHOLDER BLOCKS — timeline half ──
                  🔻 SUPPRESSED ENTIRELY AT `quarter` ZOOM (2026-08-27, on the
                  owner's own correction of my first pass). "No dates set" at
                  7 days is 35px there — `seg.width >= 35` scrapes the
                  existing label gate and the text is then clipped by
                  `overflow-hidden`, at which point the visual treatment
                  (faded + dashed, indistinguishable from an OFF week) would
                  be the SOLE carrier with no text to disambiguate it — the
                  exact ambiguity the text exists to prevent. `quarter` is
                  also neither draggable nor expandable for a dated member
                  (`DAY_EDIT_ZOOMS`), so the block would have no function
                  there either. The existing hidden-count note (controls bar,
                  above) covers this zoom instead.
                  🎨 Text is the PRIMARY carrier (owner ruling, 2026-08-27) —
                  `opacity-*`/`border-dashed` are reinforcing, not sole, so
                  reusing them here does not collide with the Off-week or
                  dirty-guard meanings. Dashed + a light grade-tinted fill,
                  distinct from Off's 0.15 so the two do not read as the same
                  intensity at a glance; the text is what actually
                  disambiguates. `title` + `sr-only` from `noDatesSetTitle`,
                  the ONE string, per this directory's `InactiveBadge.js`
                  device. */}
              {placeholderRowsVisible && membersWithoutDates.map(m => {
                const colors = getGradeColor(m);
                const isDraggingThis = placeholderDrag && placeholderDrag.memberId === m.id;
                const deltaDays = isDraggingThis ? placeholderDrag.deltaDays : 0;
                const anchorOffset = daysDiff(timelineStart, placeholderWeekStart);
                const left = (anchorOffset + deltaDays) * dayWidth;
                const width = Math.max(7 * dayWidth, 4);
                const label = NO_DATES_SET_LABEL;
                const title = noDatesSetTitle(m);
                // Same idiom as the segment-label gate elsewhere in this
                // file: >= 35px at rest, >= 20px while actively dragging.
                const showLabel = width >= (isDraggingThis ? 20 : 35);
                return (
                  <div
                    key={`placeholder-timeline-${m.id}`}
                    className="relative border-b border-gray-100 dark:border-gray-700"
                    data-testid={`crew-gantt-placeholder-timeline-row-${m.id}`}
                    style={{ height: rowHeight }}
                  >
                    <div
                      data-testid={`crew-gantt-placeholder-bar-${m.id}`}
                      role="img"
                      aria-label={title}
                      title={title}
                      /* 🪤 `colors.text` IS NOT USED HERE, DELIBERATELY — it is
                         `text-white`, calibrated for text sitting on a SOLID
                         grade-coloured fill elsewhere in this file. This bar's
                         fill is a translucent tint over the page background
                         (light or `#1e293b` dark per `index.css`'s cascade),
                         so white text would be all but invisible. A plain
                         neutral pair reads on any grade hue and either theme. */
                      className={`absolute top-1.5 rounded-md overflow-hidden flex items-center justify-center px-1 text-[11px] font-medium text-gray-700 dark:text-gray-200 border-2 border-dashed ${
                        canEdit ? 'cursor-move' : 'cursor-default'
                      } ${isDraggingThis ? 'shadow-lg' : ''}`}
                      style={{
                        left,
                        width,
                        height: rowHeight - 12,
                        /* 🪤 ALPHA IN THE COLOUR, NEVER THE CSS `opacity`
                           PROPERTY — `opacity` on this element would ALSO
                           fade the text inside it, undoing the owner's
                           ruling that the label is the PRIMARY carrier and
                           the fade/dash are merely reinforcing. `hexWithAlpha`
                           keeps the fill translucent while the text and
                           border stay fully legible. */
                        borderColor: colors.hex,
                        backgroundColor: hexWithAlpha(colors.hex, isDraggingThis ? 0.30 : 0.15),
                        zIndex: isDraggingThis ? GANTT_DRAG_Z : 5,
                      }}
                      onMouseDown={(e) => canEdit && handlePlaceholderDragStart(e, m)}
                      onTouchStart={(e) => canEdit && handlePlaceholderDragStart(e, m)}
                      {...(canEdit ? { 'data-pan-skip': '' } : {})}
                    >
                      {showLabel && <span className="truncate">{label}</span>}
                      <span className="sr-only">{title}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        </div>

        {/* ── THE LEVEL-2 DETAILS PANEL ─────────────────────────────────
            🚨 FULL WIDTH, INLINE AT ITS ROW, SPANNING THE MEMBER COLUMN AND THE
            TIMELINE. Read `DETAILS_PANEL_PLACEMENT` before touching any of this.
            🔑 IT IS A SIBLING OF THE TWO-COLUMN FLEX ROW, INSIDE THE VERTICAL
            SCROLLER — not inside the timeline (which scrolls sideways under it),
            and not inside a row (every row carries `overflow-hidden` from
            `ROW_EXPAND_TRANSITION`, which would clip it).
            🔑 `left-0 right-0` IS THE WHOLE WIDTH STORY. An absolutely positioned
            child of a scroll container is sized against that container's padding
            box and scrolls with its content, so this is 100% of the visible card
            and it still travels with its row. There is deliberately NO measured
            width here any more — `useMeasuredWidth` went with the sticky
            positioning it existed to serve.
            🚫 DO NOT nest this back inside the timeline "so it lines up with the
            bars". That reintroduces the horizontal scroller as its ancestor and
            with it every rule this change deleted. */}
        {detailsTop != null && (
          <div
            data-testid="crew-gantt-details-panel"
            data-crew-gantt-details-panel
            /* 🚧 `data-pan-skip`, for a NEW reason since 2026-08-23: it used to
               opt the panel out of the TIMELINE's pan; the panel is no longer in
               the timeline. It now opts out of the BODY scroller's vertical pan,
               without which a drag begun on the panel's padding would scroll the
               chart out from under the form. Same attribute, different scroller. */
            data-pan-skip
            className={`absolute left-0 right-0 border-t-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 ${ROW_EXPAND_TRANSITION}`}
            style={{
              /* 🚨 ONE ARITHMETIC, PLUS THE HEADER. `detailsTop` is the row's
                 cumulative offset inside the ROWS container; this element sits
                 one level further out, below the sticky date band, which is
                 exactly `headerHeight` in flow. Drop the term and the
                 panel is drawn 48px too high, straight over its own row. */
              top: headerHeight + detailsTop,
              zIndex: GANTT_PANEL_Z,
              /* 🚨 THE SAME `detailsHeight` THE ROW GROWS BY — ONE
                 NUMBER, SO THE PANEL AND ITS ROW CANNOT DESYNC.
                 Owner, 2026-08-23: the panel *"pops"* rather than
                 sliding.

                 This read `detailsBodyHeight || undefined`, which
                 resolved to `height: auto` — and **a CSS transition
                 cannot run to or from `auto`**, so the panel appeared
                 at full size on its first frame while the row was
                 still growing underneath it. The `transition-[height]`
                 in `ROW_EXPAND_TRANSITION` on this very element was
                 never inert; it simply had no numeric height to
                 animate. (The brief said there was "no transition on
                 it" — there was, and still is, unchanged.)

                 Driving it from `detailsHeight` makes the lockstep
                 STRUCTURAL rather than a matched pair of expressions:
                 first paint is 0 for both (unmeasured), the
                 measurement lands, and both go to the measured height
                 in the same render under the same 200ms ease-out.
                 `motion-reduce:transition-none` carries through
                 untouched, so a reduced-motion user still gets the
                 instant change.

                 🪤 `detailsHeight` is `detailsBodyHeight || 0` gated on
                 open AND anchor-visible, so a closed panel, an
                 unmeasured first paint and a missing anchor all
                 contribute exactly 0 — the property RULE 3 of
                 `DETAILS_PANEL_PLACEMENT` depends on. Do NOT
                 reintroduce `undefined` here to "avoid clipping": that
                 is the bug, not a safety net. */
              height: detailsHeight,
            }}
          >
            <div ref={detailsBodyRef}>{detailsPanel}</div>
          </div>
        )}
      </div>


      {/* Totals footer — mirrors the Table view's bottom row; updates live as members/drags change */}
      <div className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 px-3 py-2 flex items-center gap-6 text-sm font-semibold">
        <span className="text-gray-700 dark:text-gray-300">Total</span>
        <span className="ml-auto flex items-center gap-6 text-gray-800 dark:text-gray-200">
          <span><span className="font-normal text-gray-500 dark:text-gray-400 mr-1.5">Labour</span>{formatMoney(totalLabour || 0)}</span>
          {/* 🔑 `Allowances` INCLUDES THE LAPTOP, and the figure it is given does too —
              see the prop's docblock above. Computer rental is one type of allowance
              (owner, three times: *"computer is one type of allowance and not
              different"*), so a separate `Computer` term here presented it as a
              category of its own. Label and figure moved together; do not change one. */}
          <span><span className="font-normal text-gray-500 dark:text-gray-400 mr-1.5">Allowances</span>{formatMoney(totalAllowances || 0)}</span>
        </span>
      </div>

      {/* 📜 `pageScroll`'s sticky horizontal bar — only while the timeline
          overflows. `marginLeft: labelWidth` makes it exactly the timeline's
          width (the label column is `labelWidth` border-box), so its spacer of
          the timeline's `scrollWidth` gives the two ONE scroll range.
          🔽 BELOW the totals footer (owner, 2026-10-05: *"Can we move it below
          the 'Total / Labour £541,000.00 / Allowances £1,396.20' line"*): in flow it rests under
          Total once the chart's end is in view; while the chart runs off
          screen it still pins at the window bottom (`stickyBottom`). */}
      {pageScroll && timelineOverflows ? (
        <StickyScrollbar
          ref={stickyBarRef}
          contentStyle={{ width: timelineScrollWidth }}
          testId="crew-gantt-scrollbar"
          style={stickyBottom == null ? { marginLeft: labelWidth } : { marginLeft: labelWidth, bottom: stickyBottom }}
        />
      ) : null}

      {/* Tooltip — PORTALLED to `document.body` at the z-60 ESCAPE TIER
          (`components/ui/CLAUDE.md`'s z-index contract): the chart root is
          `isolate`, so a `fixed` child can no longer beat the app bar (z-30)
          or Toolbar (z-20). Measured on a real render: an in-tree tooltip over a
          bar near the top of the viewport lost its top 80–140px under the app
          bar; portalled it paints over the chrome exactly as before `isolate`. */}
      {tooltip && !dragState && (
        <GanttBarTooltip
          tooltip={tooltip}
          getMinTop={getPinnedBottom}
          onMouseEnter={cancelHideTooltip}
          onMouseLeave={() => setTooltip(null)}
        >
          <div className="font-semibold mb-1">{tooltip.member.name}</div>
          <div className="text-gray-300 space-y-0.5">
            <div>Grade: {tooltip.member.grade_name || '—'}</div>
            {tooltip.member.user_role_display_name && (
              <div>Role: {tooltip.member.user_role_display_name}</div>
            )}
            {tooltip.member.user_id && (
              <div>Status: <span className={
                tooltip.member.user_status === 'active' ? 'text-green-400' :
                tooltip.member.user_status === 'invited' ? 'text-yellow-400' :
                tooltip.member.user_status === 'deactivated' ? 'text-red-400' :
                'text-gray-500 dark:text-gray-400'
              }>{tooltip.member.user_status || 'placeholder'}</span></div>
            )}
            <div>Dates: {formatDateShort(tooltip.member.start_date)} → {formatDateShort(tooltip.member.end_date)}</div>
            <div>Weeks: {tooltip.member.total_weeks ?? '—'}</div>
            {tooltip.offWeeks > 0 && (
              /* "Off weeks:" until 2026-10-19 — every user-visible "Off" that
                 means allocation 0 now reads "0 days" (owner: "ok"). */
              <div>0-day weeks: {tooltip.offWeeks} (dashed)</div>
            )}
            {/*
              🐛 FIXED 2026-08-21 (N3) — this was `tooltip.member.grade_weekly_rate`,
              the FOURTH copy of the ignore-the-override bug and the one the S2-C
              sweep missed: it unified the three copies that MULTIPLY the rate and
              left the one that merely PRINTS it. The row above and below are
              `total_weeks` and `budget_cost`, both server-computed and both
              honouring `weekly_rate_override` — so an overridden member's tooltip
              read "Weeks: 2 · Rate: £1,000/wk · Budget: £2,800", three numbers
              side by side that contradict each other's arithmetic.
              🔑 Found by asking what ELSE reads `grade_weekly_rate` in this file
              while writing the guard for the three known sites — a display-only
              site is still a money claim.
            */}
            <div>Rate: {formatMoney(previewWeeklyRate(tooltip.member))}/wk</div>
            <div>Budget: {formatMoney(tooltip.member.budget_cost)}</div>
            <div>Allowance total: {formatMoney(tooltip.member.allowance_total || 0)}</div>
            {/* The zoom-independent channel for the premium marks. At quarter
                zoom a mark is 5px wide and a poor hover target; the bar is
                not. Counts only — what a premium day COSTS is the server's
                answer and is deliberately not previewed here. */}
            {premiumDaysSummary(tooltip.member) && (
              <div>Premium days: {premiumDaysSummary(tooltip.member)}</div>
            )}
          </div>
        </GanttBarTooltip>
      )}

      {/* Drag tooltip */}
      {/* Anchored to the DRAGGED BAR (owner 2026-10-05: *"the tool tips when
          dragging are appearing in what seem to be random places"*) — it was
          pinned to the VIEWPORT CENTRE (`left/top: 50%`), wherever the bar was.
          `gantt/GanttDragTooltip` tracks the bar's live rect: above it, below
          when the pinned band is in the way, never under the pointer. */}
      {dragState && dragTooltipContent && (
        <GanttDragTooltip getAnchor={getDragBarEl} getMinTop={getPinnedBottom} prefer="below" testId="crew-gantt-drag-tooltip">
          <div className={`bg-gray-900 text-white text-xs rounded-lg shadow-xl px-4 py-3 border-2 ${
            dragTooltipContent.isExtend ? 'border-blue-400' : dragTooltipContent.isShrink ? 'border-amber-400' : 'border-gray-500'
          }`}>
            <div className="font-semibold mb-1.5 flex items-center gap-2">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                dragTooltipContent.isExtend ? 'bg-blue-500' : dragTooltipContent.isShrink ? 'bg-amber-500' : 'bg-gray-500'
              }`}>
                {dragTooltipContent.action}
              </span>
              {dragTooltipContent.name}
            </div>
            <div className="text-gray-300 space-y-0.5">
              {dragTooltipContent.originalStart !== dragTooltipContent.newStart && (
                <div>Start: {dragTooltipContent.originalStart} → <span className="text-white font-medium">{dragTooltipContent.newStart}</span></div>
              )}
              {dragTooltipContent.originalEnd !== dragTooltipContent.newEnd && (
                <div>End: {dragTooltipContent.originalEnd} → <span className="text-white font-medium">{dragTooltipContent.newEnd}</span></div>
              )}
              {!dragTooltipContent.changed && (
                <div>End: {dragTooltipContent.originalEnd}</div>
              )}
              {/* 🔻 ONE FIGURE, NOT `<original> → <live>` — 2026-08-23. The
                  arrow's left-hand side was `member.budget_cost` captured at
                  drag start, which since the drag became batched is itself a
                  pending preview rather than "what it was"; and its right-hand
                  side was a client-side `weeks × rate`, the number the owner
                  reported as ignoring off days. What is left is the SERVER's
                  answer for the draft on screen. */}
              <div>
                Budget:{' '}
                <span className="text-white font-medium">
                  {dragTooltipContent.costState === 'failed'
                    ? LIVE_COST_ABSENT_MARK
                    : formatMoney(dragTooltipContent.liveBudget || 0)}
                  {dragTooltipContent.costState === 'pending' && LIVE_COST_PENDING_MARK}
                </span>
              </div>
            </div>
          </div>
        </GanttDragTooltip>
      )}
    </div>
  );
}

export default CrewGanttView;
